import { createGrid, ModuleRegistry, themeAlpine, themeBalham, themeMaterial, themeQuartz, colorSchemeDark, colorSchemeLight, colorSchemeDarkWarm, colorSchemeDarkBlue } from "ag-grid-community";
import { LicenseManager, AllEnterpriseModule } from 'ag-grid-enterprise';
import htmx from './htmxsetup.js';
import debounce from 'lodash.debounce';
import './jobcardprint.js';
import { gridPrint } from './gridprint.js';
import { fetchWithError } from './common.js';
import { isQtyAmountOrWeightColumn, parseAgGridNumber } from './aggrid_number.js';

console.log('Deployed env:', DEPLOY_ENV)
console.log("All Enterprise Modules: ", AllEnterpriseModule);
ModuleRegistry.registerModules([AllEnterpriseModule]);
if(DEPLOY_ENV === "test" || DEPLOY_ENV === "prod"){
  LicenseManager.setLicenseKey(atob(AGSK));
  console.log("AGSK: ", atob(AGSK).substring(10))
}


const themes = {
  quartz: themeQuartz,
  material: themeMaterial,
  balham: themeBalham,
  alpine: themeAlpine,
};

const colorScheme = {
  dark: colorSchemeDark,
  light: colorSchemeLight,
  darkWarm: colorSchemeDarkWarm,
  darkBlue: colorSchemeDarkBlue,
};

window.agGridThemes = themes;
window.agGridColorSchemes = colorScheme;

const GRID_NAV_CONTEXT_FIELDS = ['period', 'start_date', 'end_date', 'report'];

function clearStoredGridState(agId, { includeNavigationState = true } = {}) {
  if (!agId) return;
  if (includeNavigationState) sessionStorage.removeItem('gridState:' + agId);
  const keys = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key) keys.push(key);
  }
  keys.forEach((key) => {
    if (key.indexOf('gridState:persisted:' + agId + '|') === 0) sessionStorage.removeItem(key);
  });
}

function resetGridFiltersForElement(el) {
  const root = el?.closest?.('[id$="_entryform"],[data-formname],form,main') || document;
  const gridIds = Array.from(root.querySelectorAll?.('[data-ag-id]') || [])
    .map((grid) => grid.dataset?.agId)
    .filter(Boolean);
  window.__smaacGridStateReset = { until: Date.now() + 8000, gridIds };
  gridIds.forEach((agId) => {
    clearStoredGridState(agId);
    const api = window['aggridApi:' + agId];
    if (api && !api.isDestroyed?.() && typeof api.setFilterModel === 'function') {
      api.setFilterModel(null);
      api.onFilterChanged?.();
    }
  });
}

// Opens a grid record in a new browser tab via GET to the landing URL.
// Only sends the record id (not the full row payload) to keep URLs short
// and avoid leaking data in browser history/referrers.
function openGridRecordInNewTab(landingUrl, rowData) {
  const params = new URLSearchParams();
  params.set('showview', 'dataform');
  // Match the same identifier priority as getRowIdentifier() used elsewhere in this file
  // Use .find() to skip empty strings (rowData.id === '' would pass the ?? chain)
  const recordId = [rowData.id, rowData.link_id, rowData.pk, rowData.uuid, rowData.guid, rowData.ID, rowData.Id, rowData.awsfile_id]
    .find((v) => v != null && String(v).trim() !== '');
  if (recordId !== undefined) params.set('id', String(recordId));

  let apitoken = new URLSearchParams(window.location.search).get('apitoken');
  if (!apitoken) {
    const tokenInput = document.querySelector('[name="apitoken"]');
    if (tokenInput) apitoken = tokenInput.value;
  }
  if (apitoken) params.set('apitoken', apitoken);
  const formContainer = document.querySelector('[data-formname]');
  GRID_NAV_CONTEXT_FIELDS.forEach((fieldName) => {
    const field = formContainer && formContainer.querySelector(`[name="${fieldName}"]`);
    if (field && field.value) params.set(fieldName, field.value);
  });

  const sep = landingUrl.includes('?') ? '&' : '?';
  window.open(landingUrl + sep + params.toString(), '_blank', 'noopener');
}

function isSelectionCheckboxCellClick(params) {
  const target = params && params.event && params.event.target;
  if (target && target.closest) {
    if (target.closest('.ag-selection-checkbox, .ag-checkbox-input-wrapper')) return true;
    const cell = target.closest('.ag-cell');
    const colId = cell && cell.getAttribute && cell.getAttribute('col-id');
    if (colId && colId.indexOf('ag-Grid-SelectionColumn') !== -1) return true;
  }
  const column = params && params.column;
  const colId = column && typeof column.getColId === 'function' ? column.getColId() : '';
  if (colId && colId.indexOf('ag-Grid-SelectionColumn') !== -1) return true;
  const colDef = params && params.colDef;
  return !!(colDef && (colDef.checkboxSelection || colDef.headerCheckboxSelection));
}

const ROW_RESIZE_EDGE_PX = 6;
const ROW_RESIZE_START_PX = 2;
const ROW_RESIZE_MIN_HEIGHT = 20;
const ROW_RESIZE_MAX_HEIGHT = 240;

function bindManualRowResize(gridApi, gridEl, gridOptions) {
  if (!gridApi || !gridEl || gridOptions?.suppressRowResize === true) return;

  let resizeState = null;

  function rowFromEvent(event) {
    const rowEl = event.target?.closest?.('.ag-row');
    if (!rowEl || !gridEl.contains(rowEl)) return null;
    const rowIndex = Number(rowEl.getAttribute('row-index'));
    if (!Number.isInteger(rowIndex) || rowIndex < 0) return null;
    const node = gridApi.getDisplayedRowAtIndex?.(rowIndex);
    if (!node || node.footer || node.rowPinned) return null;
    return { rowEl, node };
  }

  function isNearRowBottom(event, rowEl) {
    const rect = rowEl.getBoundingClientRect();
    return rect.bottom - event.clientY >= 0 && rect.bottom - event.clientY <= ROW_RESIZE_EDGE_PX;
  }

  function updateResizeCursor(event) {
    if (resizeState || gridApi.isDestroyed?.()) return;
    const row = rowFromEvent(event);
    gridEl.classList.toggle(
      'smaac-ag-row-resize-hover',
      !!(row && isNearRowBottom(event, row.rowEl))
    );
  }

  function clearResizeCursor() {
    if (!resizeState) gridEl.classList.remove('smaac-ag-row-resize-hover');
  }

  function finishResize() {
    if (!resizeState) return;
    resizeState = null;
    document.body.classList.remove('smaac-ag-row-resizing');
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', finishResize);
    document.removeEventListener('pointercancel', finishResize);
    gridEl.classList.remove('smaac-ag-row-resize-hover');
  }

  function onPointerMove(event) {
    if (!resizeState) return;
    const deltaY = event.clientY - resizeState.startY;
    if (!resizeState.hasResized && Math.abs(deltaY) < ROW_RESIZE_START_PX) {
      event.preventDefault();
      return;
    }
    const nextHeight = Math.max(
      resizeState.minHeight,
      Math.min(
        ROW_RESIZE_MAX_HEIGHT,
        resizeState.startHeight + deltaY
      )
    );
    resizeState.hasResized = true;
    resizeState.node.__smaacManualRowHeight = true;
    resizeState.node.setRowHeight(nextHeight);
    gridApi.onRowHeightChanged?.();
    event.preventDefault();
  }

  function onPointerDown(event) {
    if (event.button !== 0 || event.pointerType === 'touch' || gridApi.isDestroyed?.()) return;
    const row = rowFromEvent(event);
    if (!row || !isNearRowBottom(event, row.rowEl)) return;
    const baseHeight = Number(row.node.rowHeight || row.rowEl.offsetHeight || gridOptions.rowHeight);
    resizeState = {
      node: row.node,
      startY: event.clientY,
      startHeight: Number.isFinite(baseHeight) ? baseHeight : ROW_RESIZE_MIN_HEIGHT,
      minHeight: Math.max(ROW_RESIZE_MIN_HEIGHT, Number(gridOptions.rowHeight) || ROW_RESIZE_MIN_HEIGHT),
      hasResized: false,
    };
    document.body.classList.add('smaac-ag-row-resizing');
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', finishResize);
    document.addEventListener('pointercancel', finishResize);
    event.preventDefault();
  }

  gridEl.addEventListener('pointermove', updateResizeCursor);
  gridEl.addEventListener('pointerleave', clearResizeCursor);
  gridEl.addEventListener('pointerdown', onPointerDown);
  gridApi.addEventListener('gridPreDestroyed', function () {
    finishResize();
    gridEl.removeEventListener('pointermove', updateResizeCursor);
    gridEl.removeEventListener('pointerleave', clearResizeCursor);
    gridEl.removeEventListener('pointerdown', onPointerDown);
  });
}

// ------------------------------------------------------------
// Common filter helpers
// ------------------------------------------------------------
// For the "Multiple Values" text filter option, users can paste values separated by
// newlines/spaces/commas (blank lines are OK). We treat them as OR tokens.
const splitMultiValuesInput = (raw) => {
  if (raw === null || raw === undefined) return [];
  const s = String(raw).trim();
  if (!s) return [];
  // Split on any whitespace (incl. newlines/tabs) or common separators.
  return s
    .split(/[\s,;]+/g)
    .map(v => v.trim())
    .filter(Boolean);
};

const normalizeMultiValueToken = (val) => String(val ?? '').trim().toLowerCase();

const AG_GRID_CLEAR_FILTER_OPTION = 'empty';
const AG_GRID_SIMPLE_FILTER_OPTIONS = {
  agDateColumnFilter: ['equals', AG_GRID_CLEAR_FILTER_OPTION, 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
  agNumberColumnFilter: ['equals', AG_GRID_CLEAR_FILTER_OPTION, 'notEqual', 'lessThan', 'lessThanOrEqual', 'greaterThan', 'greaterThanOrEqual', 'inRange', 'blank', 'notBlank'],
  agTextColumnFilter: ['contains', AG_GRID_CLEAR_FILTER_OPTION, 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith', 'blank', 'notBlank'],
};

const withClearFilterOption = (options) => {
  if (!Array.isArray(options) || options.length === 0) return [AG_GRID_CLEAR_FILTER_OPTION];
  if (options.includes(AG_GRID_CLEAR_FILTER_OPTION)) return options;
  const [defaultOption, ...remainingOptions] = options;
  return [defaultOption, AG_GRID_CLEAR_FILTER_OPTION, ...remainingOptions];
};

const addClearFilterOption = (colDef) => {
  const options = colDef.filterParams?.filterOptions || AG_GRID_SIMPLE_FILTER_OPTIONS[colDef.filter];
  if (!Array.isArray(options) && !AG_GRID_SIMPLE_FILTER_OPTIONS[colDef.filter]) return;
  colDef.filterParams = {
    ...(colDef.filterParams || {}),
    filterOptions: withClearFilterOption(options),
  };
};

const isAgGridBodyRow = (params) => {
  const node = params && params.node;
  return !!(node && node.level !== -1 && !node.group && !node.footer && !node.rowPinned);
};

const isAgGridEditableCell = (params) => {
  if (!isAgGridBodyRow(params)) return false;
  const editable = params && params.colDef ? params.colDef.editable : false;
  if (typeof editable === 'function') {
    try {
      return Boolean(editable(params));
    } catch (_e) {
      return false;
    }
  }
  return Boolean(editable);
};

const isAgGridRequiredFlag = (value) => {
  if (value === true || value === 1) return true;
  if (typeof value !== 'string') return false;
  return ['true', '1', 'yes', 'y'].includes(value.trim().toLowerCase());
};

const hasAgGridRequiredHeaderClass = (headerClass) => {
  if (typeof headerClass === 'string') return headerClass.split(/\s+/).includes('ag-required-header');
  if (Array.isArray(headerClass)) return headerClass.includes('ag-required-header');
  return false;
};

const isAgGridRequiredCell = (params) => {
  if (!isAgGridEditableCell(params)) return false;
  const colDef = params && params.colDef ? params.colDef : {};
  const headerName = String(colDef.headerName || '');
  return (
    isAgGridRequiredFlag(colDef.required) ||
    isAgGridRequiredFlag(colDef.mandatory) ||
    isAgGridRequiredFlag(colDef.isMandatory) ||
    isAgGridRequiredFlag(colDef.IsMandatory) ||
    hasAgGridRequiredHeaderClass(colDef.headerClass) ||
    headerName.includes('*')
  );
};

class HeaderInfoComponent {
  init(params) {
    this.eGui = document.createElement('span');
    this.eGui.className = 'smaac-grid-header-info';
    this.eGui.style.display = 'inline-flex';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.gap = '4px';
    this.eGui.style.minWidth = '0';
    this.eGui.style.maxWidth = '100%';

    this.eLabel = document.createElement('span');
    this.eLabel.className = 'smaac-grid-header-info__label';
    this.eLabel.style.overflow = 'hidden';
    this.eLabel.style.textOverflow = 'ellipsis';
    this.eLabel.textContent = params.displayName || '';
    this.eGui.appendChild(this.eLabel);

    const info = params.headerInfo || params.colDef?.headerInfo || '';
    if (info) {
      this.eButton = document.createElement('span');
      this.eButton.className = 'smaac-grid-header-info__button';
      this.eButton.textContent = 'i';
      this.eButton.style.display = 'inline-flex';
      this.eButton.style.alignItems = 'center';
      this.eButton.style.justifyContent = 'center';
      this.eButton.style.flex = '0 0 auto';
      this.eButton.style.width = '12px';
      this.eButton.style.height = '12px';
      this.eButton.style.border = '1px solid #94a3b8';
      this.eButton.style.borderRadius = '999px';
      this.eButton.style.color = '#475569';
      this.eButton.style.background = '#fff';
      this.eButton.style.fontSize = '9px';
      this.eButton.style.fontWeight = '700';
      this.eButton.style.lineHeight = '1';
      this.eButton.style.cursor = 'help';
      this.eButton.setAttribute('aria-label', info);
      this.eButton.setAttribute('role', 'img');
      this.eButton.addEventListener('click', (event) => event.stopPropagation());
      this.eButton.addEventListener('mousedown', (event) => event.stopPropagation());
      this.eButton.addEventListener('mouseenter', () => this.showTooltip(info));
      this.eButton.addEventListener('mouseleave', () => this.hideTooltip());
      this.eButton.addEventListener('focus', () => this.showTooltip(info));
      this.eButton.addEventListener('blur', () => this.hideTooltip());
      this.eGui.appendChild(this.eButton);
    }
  }

  getGui() {
    return this.eGui;
  }

  refresh(params) {
    if (this.eLabel) this.eLabel.textContent = params.displayName || '';
    const info = params.headerInfo || params.colDef?.headerInfo || '';
    if (this.eButton) {
      this.eButton.setAttribute('aria-label', info);
    }
    return true;
  }

  showTooltip(info) {
    this.hideTooltip();
    if (!info || !this.eButton) return;
    const tooltip = document.createElement('div');
    tooltip.className = 'smaac-grid-header-info__tooltip';
    tooltip.textContent = info;
    tooltip.style.position = 'fixed';
    tooltip.style.zIndex = '9999';
    tooltip.style.maxWidth = '320px';
    tooltip.style.padding = '8px 10px';
    tooltip.style.borderRadius = '4px';
    tooltip.style.background = '#111827';
    tooltip.style.color = '#fff';
    tooltip.style.fontSize = '12px';
    tooltip.style.lineHeight = '1.35';
    tooltip.style.whiteSpace = 'pre-line';
    tooltip.style.boxShadow = '0 10px 24px rgba(15, 23, 42, 0.22)';
    document.body.appendChild(tooltip);

    const buttonRect = this.eButton.getBoundingClientRect();
    const tipRect = tooltip.getBoundingClientRect();
    const margin = 8;
    let left = buttonRect.left;
    let top = buttonRect.bottom + margin;
    if (left + tipRect.width > window.innerWidth - margin) {
      left = window.innerWidth - tipRect.width - margin;
    }
    if (top + tipRect.height > window.innerHeight - margin) {
      top = buttonRect.top - tipRect.height - margin;
    }
    tooltip.style.left = `${Math.max(margin, left)}px`;
    tooltip.style.top = `${Math.max(margin, top)}px`;
    this.tooltipEl = tooltip;
  }

  hideTooltip() {
    if (this.tooltipEl && this.tooltipEl.parentNode) {
      this.tooltipEl.parentNode.removeChild(this.tooltipEl);
    }
    this.tooltipEl = null;
  }

  destroy() {
    this.hideTooltip();
  }
}

// Called from baseOnGridReady for every ag-grid instance to wire keyboard navigation.
// Lives on window because page-specific bundles (indentssr.js, mrs.js, etc.) also call it.
window.setupTabNavigation = function(gridApi) {
  if (!gridApi || !gridApi.addEventListener) return;
  if (gridApi._tabNavBound) return;
  gridApi._tabNavBound = true;

  const isEditableForRow = (colDef, rowData) => {
    const e = colDef.editable;
    if (!e) return false;
    if (e === true) return true;
    if (typeof e === 'function') {
      try { return Boolean(e({ data: rowData, colDef })); } catch (_) { return false; }
    }
    return false;
  };

  // Tab: skip non-editable columns, evaluate per-row editable conditions.
  // Uses getAllDisplayedColumns() so hidden columns and custom colIds are handled correctly.
  gridApi.addEventListener('cellKeyDown', (params) => {
    if (params.event.key !== 'Tab') return;
    const { rowIndex, column } = params;
    const allCols = params.api.getAllDisplayedColumns();
    const isShiftPressed = params.event.shiftKey;
    const rowData = params.node ? params.node.data : null;

    const currentColIndex = allCols.findIndex(col => col.getColId() === column.getColId());
    let nextColumn, nextRow;

    if (isShiftPressed) {
      let prevColIndex = currentColIndex - 1;
      while (prevColIndex >= 0) {
        if (isEditableForRow(allCols[prevColIndex].getColDef(), rowData)) {
          nextColumn = allCols[prevColIndex];
          nextRow = rowIndex;
          break;
        }
        prevColIndex--;
      }
      if (!nextColumn && rowIndex > 0) {
        nextRow = rowIndex - 1;
        const prevNode = params.api.getDisplayedRowAtIndex(nextRow);
        const prevRowData = prevNode ? prevNode.data : null;
        for (let i = allCols.length - 1; i >= 0; i--) {
          if (isEditableForRow(allCols[i].getColDef(), prevRowData)) {
            nextColumn = allCols[i];
            break;
          }
        }
      }
    } else {
      let nextColIndex = currentColIndex + 1;
      while (nextColIndex < allCols.length) {
        if (isEditableForRow(allCols[nextColIndex].getColDef(), rowData)) {
          nextColumn = allCols[nextColIndex];
          nextRow = rowIndex;
          break;
        }
        nextColIndex++;
      }
      if (!nextColumn && rowIndex < params.api.getDisplayedRowCount() - 1) {
        nextRow = rowIndex + 1;
        const nextNode = params.api.getDisplayedRowAtIndex(nextRow);
        const nextRowData = nextNode ? nextNode.data : null;
        for (let i = 0; i < allCols.length; i++) {
          if (isEditableForRow(allCols[i].getColDef(), nextRowData)) {
            nextColumn = allCols[i];
            break;
          }
        }
      }
    }

    if (nextColumn && nextRow !== undefined) {
      params.event.preventDefault();
      const colId = nextColumn.getColId();
      setTimeout(() => {
        params.api.stopEditing(false);
        params.api.setFocusedCell(nextRow, colId);
        params.api.startEditingCell({ rowIndex: nextRow, colKey: colId });
      }, 10);
    }
  });

  // ArrowDown/Up/Left/Right: navigate rows/columns while a cell is being edited.
  // Named so it can be removed on grid destroy to avoid listener accumulation.
  const gridEl = gridApi.getGridElement?.();
  if (gridEl) {
    const onGridKeyDown = (e) => {
      const key = e.key;
      const isVertical = key === 'ArrowDown' || key === 'ArrowUp';
      const isHorizontal = key === 'ArrowLeft' || key === 'ArrowRight';
      if (!isVertical && !isHorizontal) return;

      // Let native editors handle their own arrow keys — listener runs in capture phase.
      if (e.target instanceof HTMLSelectElement || e.target instanceof HTMLTextAreaElement) return;

      const editingCells = gridApi.getEditingCells?.() || [];
      if (editingCells.length === 0) return;
      const cell = editingCells[0];

      if (isVertical) {
        e.preventDefault();
        e.stopImmediatePropagation();
        const direction = key === 'ArrowDown' ? 1 : -1;
        const colId = cell.column.getId();
        const colDef = cell.column.getColDef();
        let nextRow = cell.rowIndex + direction;
        while (nextRow >= 0 && nextRow < gridApi.getDisplayedRowCount()) {
          const node = gridApi.getDisplayedRowAtIndex(nextRow);
          if (isEditableForRow(colDef, node ? node.data : null)) break;
          nextRow += direction;
        }
        if (nextRow < 0 || nextRow >= gridApi.getDisplayedRowCount()) return;
        // Commit current cell and move focus only — don't startEditingCell so dropdown
        // cells don't auto-open. User presses Enter or types to enter edit mode.
        setTimeout(() => {
          gridApi.stopEditing(false);
          gridApi.setFocusedCell(nextRow, colId);
        }, 10);
      } else {
        // For text-cursor inputs (selectionStart is non-null), only navigate at boundary.
        // Checkbox/radio inputs have selectionStart===null — navigate immediately like numbers.
        if (e.target instanceof HTMLInputElement && e.target.selectionStart !== null) {
          const atStart = e.target.selectionStart === 0 && e.target.selectionEnd === 0;
          const atEnd = e.target.selectionStart === e.target.value.length && e.target.selectionEnd === e.target.value.length;
          if (key === 'ArrowLeft' && !atStart) return;
          if (key === 'ArrowRight' && !atEnd) return;
        }
        // Always prevent scroll when editing, even if no navigation target is found.
        e.preventDefault();
        e.stopImmediatePropagation();
        const allCols = gridApi.getAllDisplayedColumns();
        const currentColIndex = allCols.findIndex(col => col.getId() === cell.column.getId());
        const direction = key === 'ArrowRight' ? 1 : -1;
        const rowNode = gridApi.getDisplayedRowAtIndex(cell.rowIndex);
        const rowData = rowNode ? rowNode.data : null;
        let nextCol = null;
        let i = currentColIndex + direction;
        while (i >= 0 && i < allCols.length) {
          if (isEditableForRow(allCols[i].getColDef(), rowData)) {
            nextCol = allCols[i];
            break;
          }
          i += direction;
        }
        if (!nextCol) return;
        const colId = nextCol.getId();
        const rowIndex = cell.rowIndex;
        // Commit current cell and move focus only — don't startEditingCell so dropdown
        // cells don't auto-open. User presses Enter or types to enter edit mode.
        setTimeout(() => {
          gridApi.stopEditing(false);
          gridApi.setFocusedCell(rowIndex, colId);
        }, 10);
      }
    };
    gridEl.addEventListener('keydown', onGridKeyDown, true);
    gridApi.addEventListener?.('gridPreDestroyed', () => {
      gridEl.removeEventListener('keydown', onGridKeyDown, true);
    });
  }
};

// Shared helpers for weight calculations across grids
// Central rule: whenever qty and unitweight are both > 0,
// prefer weight = qty × unitweight, rounded to 3 decimals.
window.computeWeightFromQtyUnitweight = function (row) {
  if (!row) return 0;

  const qty = parseFloat(row.qty) || 0;
  const unitweightRaw = row.unitweight;
  const unitweight = unitweightRaw === null || unitweightRaw === undefined || unitweightRaw === ''
    ? 0
    : parseFloat(unitweightRaw) || 0;

  if (qty > 0 && unitweight > 0) {
    const weight = Math.round(qty * unitweight * 1000) / 1000;
    return weight;
  }

  // Fallback to existing weight when we cannot safely recompute
  const existingWeight = row.weight;
  const parsedExisting = existingWeight === null || existingWeight === undefined || existingWeight === ''
    ? 0
    : parseFloat(existingWeight) || 0;
  return parsedExisting;
};

function syncAwsAttachmentCountsForApi(api) {
  if (!api || typeof api.forEachNode !== 'function' || api.isDestroyed?.()) return;
  const changed = [];
  api.forEachNode(node => {
    if (!node || !node.data || node.rowPinned) return;
    const rowId = node.data['awsfile_count.row_id'];
    if (rowId === undefined || rowId === null || rowId === '') return;
    const badge = document.getElementById('attachment-count-' + rowId);
    if (!badge) return;
    const match = String(badge.textContent || '').match(/\d+/);
    if (!match) return;
    const count = Number(match[0]);
    if (!Number.isFinite(count)) return;
    if (Number(node.data.awsfile_count || 0) !== count) {
      node.data.awsfile_count = count;
      changed.push(node);
    }
  });
  if (changed.length && typeof api.refreshCells === 'function') {
    api.refreshCells({ rowNodes: changed, force: true });
  }
}

// Helper to recompute and update the rendered weight footer total for a grid
function updateWeightFooterForApi(api) {
  if (!api || typeof api.forEachNode !== 'function') return;

  let totalWeight = 0;
  api.forEachNode(node => {
    if (node && node.data) {
      const w = parseFloat(node.data.weight);
      if (!isNaN(w)) {
        totalWeight += w;
      }
    }
  });

  // Best-effort: patch the last (grand total) row's weight cell for this grid
  setTimeout(() => {
    try {
      let container = null;
      // Prefer the grid's own center container when available
      if (api.gridBodyCtrl && api.gridBodyCtrl.centerRowContainerComp) {
        container = api.gridBodyCtrl.centerRowContainerComp.eContainer;
      }
      // Fallback: first center container on the page
      if (!container) {
        container = document.querySelector('.ag-center-cols-container');
      }
      if (!container) return;

      const footerRows = container.querySelectorAll('.ag-row.ag-row-last');
      if (!footerRows.length) return;
      const footerRow = footerRows[footerRows.length - 1];

      const weightCells = footerRow.querySelectorAll('[col-id="weight"]');
      if (weightCells.length > 0) {
        weightCells[0].textContent = totalWeight.toFixed(1);
      }

      // Also keep the read-only "Total Wt. (Kgs)" summary field in sync, if present.
      const totalKgEl = document.getElementById('totalkg');
      if (totalKgEl) {
        // Simple formatting with up to 2 decimals; server-side summary logic still
        // runs independently, but this keeps the visible value aligned with the grid.
        totalKgEl.textContent = totalWeight.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        });
      }
    } catch (_e) {
      // Footer patch is best-effort; ignore failures
    }
  }, 40);
}

// Generic valueGetter that can be attached to any weight column.
// It uses the same rule as computeWeightFromQtyUnitweight and also
// keeps the underlying row data in sync so summaries/exports see it.
window.weightValueGetter = function (params) {
  if (!params || !params.data) return 0;
  const newWeight = window.computeWeightFromQtyUnitweight(params.data);
  if (params.data.weight !== newWeight) {
    params.data.weight = newWeight;
  }

   // After recalculating row weight, refresh this grid's weight footer total
   if (params.api) {
     updateWeightFooterForApi(params.api);
   }

  return newWeight;
};

export const ag = {
  Grid: createGrid, // Export the Grid constructor
  reinitializeHtmx: debounce(function(containerSelector = '.ag-center-cols-viewport') {
    const container = document.querySelector(containerSelector);
    if (container) {
      htmx.process(container);
    }
    // Also reprocess any attachment buttons specifically
    const attachmentButtons = document.querySelectorAll('.attachment-btn');
    attachmentButtons.forEach(button => {
      htmx.process(button);
    });
  }, 150),
  commonStatusBadgeMap: {
    draft: {
      bgBadgeColor: "bg-gray-500 ring-1 ring-gray-600",
      textBadgeColor: "text-white",
      icon: "📝",
    },
    approved: {
      bgBadgeColor: "bg-green-600 ring-1 ring-green-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    completed: {
      bgBadgeColor: "bg-green-600 ring-1 ring-green-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    active: {
      bgBadgeColor: "bg-green-600 ring-1 ring-green-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    submitted: {
      bgBadgeColor: "bg-yellow-400 ring-1 ring-yellow-500",
      textBadgeColor: "text-white",
      icon: "📤",
    },
    cancelled: {
      bgBadgeColor: "bg-red-500 ring-1 ring-red-500",
      textBadgeColor: "text-white",
      icon: "❌",
    },
    "1st approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "first approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    approved1: {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "2nd approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "second approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    approved2: {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "3rd approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "third approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    approved3: {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "hr approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "marketing approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "plant approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "design approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    "project approved": {
      bgBadgeColor: "bg-blue-600 ring-1 ring-blue-700",
      textBadgeColor: "text-white",
      icon: "✓",
    },
    closed: {
      bgBadgeColor: "bg-stone-950 ring-1 ring-stone-950",
      textBadgeColor: "text-white",
      icon: "🔒",
    },
    rejected: {
      bgBadgeColor: "bg-red-600 ring-1 ring-red-700",
      textBadgeColor: "text-white",
      icon: "❌",
    },
  },
  normalizeStatusBadgeKey: function (value) {
    return String(value ?? '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim()
      .toLowerCase()
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ');
  },
  isStatusBadgeColumn: function (column) {
    const field = String(column?.field ?? '').trim().toLowerCase();
    const headerName = String(column?.headerName ?? '').trim().toLowerCase();
    return field === 'status' || headerName === 'status';
  },
  resolveCommonStatusBadge: function (column, value) {
    if (!this.isStatusBadgeColumn(column)) return null;
    const statusKey = this.normalizeStatusBadgeKey(value);
    return this.commonStatusBadgeMap[statusKey] || null;
  },

  // data-needs-init="ag.initBackLink" handler for link_header_buttons back links.
  // Uses window._gridEntryLandingUrl (set by aggrid.js on row click) so the back
  // link returns to the correct date-filtered listing URL instead of the bare landing.
  initBackLink: function (a) {
    a.addEventListener('click', function (event) {
      resetGridFiltersForElement(a);
      var storedUrl = (history.state && history.state.landingUrl) || window._gridEntryLandingUrl;
      if (!storedUrl) return; // let the <a href> navigate normally
      event.preventDefault();
      try {
        var u = new URL(storedUrl, window.location.origin);
        u.searchParams.set('showview', 'list');
        window._gridEntryLandingUrl = null;
        window.location.replace(u.toString());
      } catch (_e) {
        window.location.replace(a.href);
      }
    });
  },

  // data-needs-init="ag.initBackButton" handler for HTMX-based back buttons.
  // Moves the inline <script> from back.html into JS, reads includeShowview from
  // data-include-showview attribute set by the template.
  initBackButton: function (el) {
    if (el.dataset.backHandlerBound === '1') return;
    el.dataset.backHandlerBound = '1';

    var shouldHardNavigate = el.id === 'dataform-backButton';
    var includeShowview = el.dataset.includeShowview !== 'false';

    function stripDataformShowview() {
      try {
        var currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.get('showview') === 'dataform') {
          currentUrl.searchParams.delete('showview');
          window.history.replaceState({}, document.title, currentUrl.toString());
        }
      } catch (e) {
        console.error('Failed to strip showview=dataform on back button click', e);
      }
    }

    function applyIncludedFields(targetUrl) {
      try {
        var includeSelector = el.getAttribute('hx-include') || '';
        if (!includeSelector) return targetUrl;

        var params = new URLSearchParams(targetUrl.search);
        var nodes = document.querySelectorAll(includeSelector);
        nodes.forEach(function (field) {
          if (!field || !field.name) return;

          var type = (field.type || '').toLowerCase();
          if ((type === 'checkbox' || type === 'radio') && !field.checked) {
            return;
          }

          if (field.tagName === 'SELECT' && field.multiple) {
            params.delete(field.name);
            Array.from(field.selectedOptions || []).forEach(function (opt) {
              params.append(field.name, opt.value);
            });
            return;
          }

          params.set(field.name, field.value == null ? '' : field.value);
        });

        if (includeShowview) {
          params.set('showview', 'list');
        }
        targetUrl.search = params.toString();
      } catch (e) {
        console.error('Failed to build back button navigation URL', e);
      }
      return targetUrl;
    }

    el.addEventListener('click', function (event) {
      resetGridFiltersForElement(el);

      // Prefer history.state.landingUrl (persisted by aggrid.js afterSwap) so an aborted
      // unsaved-changes exit doesn't lose the stored URL on the next attempt.
      // Keep window._gridEntryLandingUrl alive until navigation actually commits.
      var storedUrl = (history.state && history.state.landingUrl) || window._gridEntryLandingUrl;

      // For HTMX-based back buttons (shouldHardNavigate=false, e.g. jobcard), inject
      // date params into hx-vals before HTMX processes the click (capture fires first).
      // The OOB swap may have removed the date filter from the DOM so hx-include can't find them.
      if (!shouldHardNavigate && storedUrl) {
        try {
          var storedSearch = new URL(storedUrl, window.location.origin).searchParams;
          var existingValsRaw = el.getAttribute('hx-vals') || '{}';
          if (!existingValsRaw.startsWith('js:')) {
            var vals = {};
            try { vals = JSON.parse(existingValsRaw); } catch (_) {}
            GRID_NAV_CONTEXT_FIELDS.forEach(function(f) {
              var v = storedSearch.get(f);
              if (v) vals[f] = v;
            });
            el.setAttribute('hx-vals', JSON.stringify(vals));
          }
        } catch (_e) {}
      }

      stripDataformShowview();
      if (!shouldHardNavigate) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      try {
        // Prefer the stored landing URL (set by aggrid.js on row click) which includes
        // the user-selected date filter params; fall back to the button's hx-get/hx-post.
        var rawUrl = storedUrl || el.getAttribute('hx-get') || el.getAttribute('hx-post') || window.location.href;
        var nextUrl = new URL(rawUrl, window.location.origin);
        nextUrl = applyIncludedFields(nextUrl);
        // Re-apply date params from storedUrl — dataform hidden inputs (e.g. <input name="period" value="">)
        // can overwrite them with empty values inside applyIncludedFields.
        if (storedUrl) {
          try {
            var storedParams = new URL(storedUrl, window.location.origin).searchParams;
            GRID_NAV_CONTEXT_FIELDS.forEach(function(f) {
              var v = storedParams.get(f);
              if (v) nextUrl.searchParams.set(f, v);
            });
          } catch (_e2) {}
        }
        if (!window.smaacPersistentNavigateTo) {
          window.location.replace(nextUrl.toString());
          return false;
        }
        if (nextUrl.toString() === window.location.href) {
          window.location.reload();
          return false;
        }
        Promise.resolve(window.smaacPersistentNavigateTo(nextUrl.toString())).then(function(didNavigate) {
          if (didNavigate) {
            window._gridEntryLandingUrl = null;
          } else {
            window.location.replace(nextUrl.toString());
          }
        }).catch(function() {
          window.location.replace(nextUrl.toString());
        });
      } catch (e) {
        console.error('Failed to hard navigate on back button click', e);
      }
      return false;
    }, true);
  },

  gridInit: function (el) {
    let selectedRows = [];

    // ------------------------------------------------------------
    // Grid JSON helpers. Prefer JSON script payloads; keep base64 attrs
    // as a compatibility fallback for older partials.
    // ------------------------------------------------------------
    const parseJsonText = (text, fallback) => {
      if (!text) return fallback;
      try {
        let parsed = JSON.parse(String(text));
        // Sometimes payloads can become double-encoded ("[{...}]" as a JSON string).
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }
        return parsed;
      } catch (e) {
        console.error('aggrid: failed to parse json payload', e);
        return fallback;
      }
    };

    const parseJsonScript = (scriptId, fallback) => {
      if (!scriptId) return fallback;
      const script = document.getElementById(String(scriptId));
      if (!script) return fallback;
      return parseJsonText(script.textContent || '', fallback);
    };

    const parseBase64Json = (b64, fallback) => {
      if (!b64) return fallback;
      try {
        const decoded = atob(String(b64));
        return parseJsonText(decoded, fallback);
      } catch (e) {
        console.error('aggrid: failed to parse base64 json', e);
        return fallback;
      }
    };

    const parseGridJsonPayload = (scriptId, b64, fallback) => {
      const fromScript = parseJsonScript(scriptId, null);
      if (fromScript !== null) return fromScript;
      return parseBase64Json(b64, fallback);
    };

    const expandCompactRows = (val) => {
      if (!val || typeof val !== 'object' || !val.__stdgrid_compact_rows__) return val;
      const columns = Array.isArray(val.columns) ? val.columns : [];
      const rows = Array.isArray(val.rows) ? val.rows : [];
      return rows.map((row) => {
        const out = {};
        for (let i = 0; i < columns.length; i++) {
          out[columns[i]] = Array.isArray(row) ? row[i] : undefined;
        }
        return out;
      });
    };

    const coerceRowArray = (val) => {
      val = expandCompactRows(val);
      // Accept common shapes and normalize to an array of row objects.
      if (Array.isArray(val)) return val;
      if (val && typeof val === 'object') {
        if (val.get_list && Array.isArray(val.get_list.result)) return val.get_list.result;
        if (Array.isArray(val.result)) return val.result;
      }
      return [];
    };

    const agGridOptions = (() => {
      const parsed = parseGridJsonPayload(
        el?.dataset?.agGridOptionsScript,
        el?.dataset?.agGridOptions,
        {}
      );
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    })();

    const getSelectionSummaryStatusBar = () => ({
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
        { statusPanel: 'agTotalRowCountComponent', align: 'center' },
        { statusPanel: 'agFilteredRowCountComponent' },
        { statusPanel: 'agSelectedRowCountComponent' },
        {
          statusPanel: 'agAggregationComponent',
          statusPanelParams: {
            aggFuncs: ['count', 'sum', 'min', 'max', 'avg'],
          },
        },
      ],
    });

    const applySelectionSummaryDefaults = (options) => {
      if (!options || options.suppressSelectionSummaryStatusBar === true || options.statusBar === false) {
        return;
      }
      if (options.enableRangeSelection !== false) {
        if (options.enableRangeSelection === undefined) {
          options.enableRangeSelection = true;
        }
        if (options.cellSelection === undefined) {
          options.cellSelection = true;
        }
      }
      if (!options.statusBar) {
        options.statusBar = getSelectionSummaryStatusBar();
        return;
      }
      const panels = Array.isArray(options.statusBar.statusPanels)
        ? options.statusBar.statusPanels
        : [];
      const aggregationPanel = panels.find(
        panel => panel && panel.statusPanel === 'agAggregationComponent'
      );
      if (!aggregationPanel) {
        panels.push(getSelectionSummaryStatusBar().statusPanels[4]);
        options.statusBar.statusPanels = panels;
        return;
      }
      aggregationPanel.statusPanelParams = {
        ...(aggregationPanel.statusPanelParams || {}),
        aggFuncs: (
          aggregationPanel.statusPanelParams
          && Array.isArray(aggregationPanel.statusPanelParams.aggFuncs)
          && aggregationPanel.statusPanelParams.aggFuncs.length
        )
          ? aggregationPanel.statusPanelParams.aggFuncs
          : ['count', 'sum', 'min', 'max', 'avg'],
      };
    };

    // Grid state save/restore for preserving position when returning from dataform
    const GRID_STATE_PREFIX = 'gridState:';
    const PERSISTED_GRID_STATE_PREFIX = 'gridState:persisted:';
    const GRID_STATE_TTL_MS = 30 * 60 * 1000;
    const GRID_STATE_FIELDS = ['tab', 'report', 'report_type', 'summarytab'];
    let _savedScrollRow = null; // Set in onGridReady, used in onFirstDataRendered

    function getGridStateAttr(node, name) {
      return node ? node.getAttribute(name) || node.getAttribute('data-' + name) || '' : '';
    }

    function getGridStateHxValue(hxVals, name) {
      const quotedName = '["\\\']?' + name + '["\\\']?';
      const quotedValue = '["\\\']([^"\\\']+)["\\\']';
      const pattern = new RegExp(quotedName + '\\s*:\\s*' + quotedValue);
      const match = (hxVals || '').match(pattern);
      return match ? match[1] : '';
    }

    function getGridStateValuesFromElement(node) {
      const values = {};
      if (!node) return values;
      const hxVals = getGridStateAttr(node, 'hx-vals');
      GRID_STATE_FIELDS.forEach((name) => {
        const value = getGridStateHxValue(hxVals, name);
        if (value) values[name] = value;
      });
      if (node.dataset && !values.tab) {
        if (node.dataset.tab) {
          values.tab = node.dataset.tab;
        } else {
          Object.keys(node.dataset).some((key) => {
            if (key.toLowerCase().endsWith('tab') && node.dataset[key]) {
              values.tab = node.dataset[key];
              return true;
            }
            return false;
          });
        }
      }
      return values;
    }

    function buildGridStateTab(values) {
      return GRID_STATE_FIELDS
        .filter((name) => values[name])
        .map((name) => name + '=' + encodeURIComponent(values[name]))
        .join('&');
    }

    function getGridStateRoot() {
      return (
        el.closest('[data-tab-cache-root][id]') ||
        el.closest('[hx-swap-oob][id][hx-get]') ||
        el.closest('[data-hx-swap-oob][id][data-hx-get]') ||
        el.closest('[id][data-formname][hx-swap-oob]') ||
        el.closest('[id][data-formname][data-hx-swap-oob]') ||
        el.closest('[id][hx-trigger*="reload"]') ||
        el.closest('[id][data-hx-trigger*="reload"]') ||
        el.closest('[hx-swap-oob][hx-get]') ||
        el.closest('[data-hx-swap-oob][data-hx-get]') ||
        el.closest('[hx-trigger*="reload"]') ||
        el.closest('[data-hx-trigger*="reload"]')
      );
    }

    function getGridStateTab(root) {
      const values = {};
      if (!root) return '';
      GRID_STATE_FIELDS.forEach((name) => {
        const hidden = root.querySelector('input[name="' + name + '"]');
        if (hidden && hidden.value) values[name] = hidden.value;
      });
      Object.assign(values, getGridStateValuesFromElement(root));
      root.querySelectorAll('.tab-link.active,[aria-selected="true"],[data-active-tab]').forEach((active) => {
        Object.assign(values, getGridStateValuesFromElement(active));
      });
      return buildGridStateTab(values);
    }

    function getGridStateRootId(root) {
      if (!root) return '';
      const formName = root.getAttribute('data-formname')
        || (root.querySelector('[data-formname]') || {}).dataset?.formname
        || (root.closest('[data-formname]') || {}).dataset?.formname
        || '';
      const rootName = root.id
        || root.getAttribute('data-tab-cache-root')
        || getGridStateAttr(root, 'hx-get')
        || getGridStateAttr(root, 'hx-trigger')
        || formName
        || root.tagName;
      return [window.location.pathname || '', formName, rootName].join('|');
    }

    function getPersistentGridStateKey(agId) {
      const root = getGridStateRoot();
      return PERSISTED_GRID_STATE_PREFIX + [
        agId || '',
        getGridStateRootId(root),
        getGridStateTab(root),
      ].join('|');
    }

    function isGridStateResetPending(agId) {
      const reset = window.__smaacGridStateReset;
      if (!reset || reset.until < Date.now()) return false;
      const ids = Array.isArray(reset.gridIds) ? reset.gridIds : [];
      return ids.length === 0 || ids.indexOf(agId) !== -1;
    }

    function getColumnSignature(api) {
      try {
        if (!api || typeof api.getColumns !== 'function') return '';
        return (api.getColumns() || [])
          .map((column) => column && typeof column.getColId === 'function' ? column.getColId() : '')
          .filter(Boolean)
          .join('|');
      } catch (_e) {
        return '';
      }
    }

    function getGridStateSnapshot(api, agId) {
      if (!api || !agId || api.isDestroyed?.()) return null;
      return {
        filterModel: {},
        columnState: typeof api.getColumnState === 'function' ? api.getColumnState() : null,
        firstRow: typeof api.getFirstDisplayedRowIndex === 'function' ? api.getFirstDisplayedRowIndex() : null,
        columnSignature: getColumnSignature(api),
        ts: Date.now()
      };
    }

    // Build the return-to-listing URL for back-button navigation, embedding the
    // current date filter and API token so the server re-renders with the right data.
    function buildLandingUrlForReturn(landingUrl) {
      try {
        let urlWithToken = landingUrl;
        const urlParams = new URLSearchParams(window.location.search);
        const apitoken = urlParams.get('apitoken')
          || (document.querySelector('[name="apitoken"]') || {}).value
          || null;
        if (apitoken) {
          const u = new URL(landingUrl, window.location.origin);
          u.searchParams.set('apitoken', apitoken);
          urlWithToken = u.toString();
        }
        const final = new URL(urlWithToken, window.location.origin);
        const formContainer = el.closest('[data-formname]');
        GRID_NAV_CONTEXT_FIELDS.forEach(field => {
          const input = formContainer && formContainer.querySelector(`[name="${field}"]`);
          if (input && input.value) final.searchParams.set(field, input.value);
        });
        return final.toString();
      } catch (_e) {
        return landingUrl;
      }
    }

    function saveGridStateBeforeNavigation(api, agId) {
      try {
        var state = getGridStateSnapshot(api, agId);
        if (!state) return;
        sessionStorage.setItem(GRID_STATE_PREFIX + agId, JSON.stringify(state));
      } catch (e) {
        // Silent fail — grid will just reload without saved position
      }
    }

    // Shared by onCellClicked and onRowDoubleClicked: append date-filter params,
    // handle Ctrl/Cmd+Click (opens in new tab), store landing URL, save grid state.
    // Returns true if the caller should return immediately (new-tab navigation handled).
    function preserveGridNavContext(searchParams, params) {
      try {
        const formContainer = el.closest('[data-formname]');
        if (formContainer) {
          GRID_NAV_CONTEXT_FIELDS.forEach((fieldName) => {
            const field = formContainer.querySelector(`[name="${fieldName}"]`);
            if (field && field.value) {
              searchParams.set(fieldName, field.value);
            }
          });
        }
      } catch (e) {
        console.error('Failed to append date filter params for grid navigation', e);
      }

      if (params.event && (params.event.ctrlKey || params.event.metaKey) && el.dataset.landingUrl) {
        openGridRecordInNewTab(el.dataset.landingUrl, params.data);
        return true;
      }

      if (el.dataset.landingUrl) {
        const formContainer = el.closest('[data-formname]');
        window._gridEntryLandingUrl = buildLandingUrlForReturn(el.dataset.landingUrl);
        window._gridEntrySlug = formContainer ? formContainer.getAttribute('data-formname') : null;
      }

      saveGridStateBeforeNavigation(params.api, el.dataset.agId);
      return false;
    }

    function tryRestoreGridState(api, agId) {
      // Returns the saved scroll row index, or null if no state to restore
      try {
        var key = GRID_STATE_PREFIX + agId;
        var raw = sessionStorage.getItem(key);
        if (!raw) return null;
        sessionStorage.removeItem(key);

        var state = JSON.parse(raw);
        if (Date.now() - state.ts > GRID_STATE_TTL_MS) return null;

        if (state.columnState) {
          api.applyColumnState({ state: state.columnState, applyOrder: true });
        }
        return typeof state.firstRow === 'number' ? state.firstRow : null;
      } catch (e) {
        return null;
      }
    }

    function savePersistentGridStateNow(api, agId) {
      try {
        if (gridOptions.disablePersistentGridState) return;
        if (isGridStateResetPending(agId)) return;
        var state = getGridStateSnapshot(api, agId);
        if (!state) return;
        sessionStorage.setItem(getPersistentGridStateKey(agId), JSON.stringify(state));
      } catch (_e) {}
    }

    const savePersistentGridState = debounce(savePersistentGridStateNow, 250);

    function restorePersistentGridState(api, agId) {
      try {
        if (gridOptions.disablePersistentGridState) return null;
        if (isGridStateResetPending(agId)) return null;
        var key = getPersistentGridStateKey(agId);
        var raw = sessionStorage.getItem(key);
        if (!raw) return null;

        var state = JSON.parse(raw);
        if (Date.now() - state.ts > GRID_STATE_TTL_MS) {
          sessionStorage.removeItem(key);
          return null;
        }

        var currentSignature = getColumnSignature(api);
        var savedSignature = state.columnSignature || '';
        if (state.columnState && typeof api.applyColumnState === 'function') {
          var stateToApply = state.columnState;
          if (currentSignature && savedSignature && currentSignature !== savedSignature) {
            var currentColumns = new Set(currentSignature.split('|').filter(Boolean));
            stateToApply = state.columnState.filter((col) => currentColumns.has(col.colId));
          }
          if (stateToApply.length > 0) {
            api.applyColumnState({ state: stateToApply, applyOrder: currentSignature === savedSignature });
          }
        }

        return typeof state.firstRow === 'number' ? state.firstRow : null;
      } catch (_e) {
        return null;
      }
    }

    const COLUMN_SIZING_DEBOUNCE_MS = 24;
    const COLUMN_SIZING_ANIMATION_MS = 240;
    let scheduledColumnSizingFrame = null;
    let activeColumnSizingAnimationFrame = null;
    let hasCompletedInitialColumnSizing = false;
    let hasSeenFirstDataRendered = false;

    const prefersReducedMotion = () => {
      try {
        return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      } catch (_e) {
        return false;
      }
    };

    const setColumnSizingAnimationState = (enabled) => {
      try {
        el?.classList.toggle('ag-column-sizing-animating', enabled);
      } catch (_e) {}
    };

    const setInitialColumnSizingPendingState = (enabled) => {
      try {
        el?.classList.toggle('ag-initial-column-sizing-pending', enabled);
      } catch (_e) {}
    };

    const cancelColumnSizingAnimation = () => {
      if (activeColumnSizingAnimationFrame !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(activeColumnSizingAnimationFrame);
      }
      activeColumnSizingAnimationFrame = null;
      setColumnSizingAnimationState(false);
    };

    const getDisplayedColumns = (gridApi) => {
      if (!gridApi || typeof gridApi.getAllDisplayedColumns !== 'function') return [];
      return gridApi.getAllDisplayedColumns() || [];
    };

    const getDisplayedColumnWidths = (gridApi) => {
      return getDisplayedColumns(gridApi).map((column) => ({
        column,
        colId: column.getColId(),
        width: column.getActualWidth(),
      }));
    };

    const setDisplayedColumnWidths = (gridApi, columnWidths, finished = true) => {
      if (!gridApi || typeof gridApi.setColumnWidths !== 'function') return;
      if (!Array.isArray(columnWidths) || columnWidths.length === 0) return;
      gridApi.setColumnWidths(
        columnWidths.map(({ column, width }) => ({
          key: column,
          newWidth: Math.max(40, Math.round(width)),
        })),
        finished,
        'api',
      );
    };

    const relaxColumnMaxWidthAfterInitialSizing = (gridApi) => {
      const columns = (
        (typeof gridApi?.getColumns === 'function' && gridApi.getColumns()) ||
        (typeof gridApi?.getAllGridColumns === 'function' && gridApi.getAllGridColumns()) ||
        getDisplayedColumns(gridApi)
      ) || [];
      columns.forEach((column) => {
        if (!column) return;
        try {
          column.maxWidth = Number.MAX_SAFE_INTEGER;
          const colDef = typeof column.getColDef === 'function' ? column.getColDef() : null;
          if (colDef && colDef.maxWidth !== undefined) {
            colDef.initialMaxWidth = colDef.initialMaxWidth ?? colDef.maxWidth;
            delete colDef.maxWidth;
          }
        } catch {
          // Best-effort only; some AG Grid column implementations may not expose mutable internals.
        }
      });
    };

    const runOnNextFrame = (cb) => (
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(cb)
        : window.setTimeout(cb, 16)
    );

    const easeInOutSine = (t) => (
      -(Math.cos(Math.PI * t) - 1) / 2
    );

    const animateColumnSizing = (gridApi, fromWidths, toWidths) => {
      if (!gridApi || gridApi.isDestroyed?.()) return;
      if (!Array.isArray(fromWidths) || !Array.isArray(toWidths) || fromWidths.length === 0 || toWidths.length === 0) return;

      const toWidthMap = new Map(toWidths.map(({ colId, width }) => [colId, width]));
      const animationColumns = fromWidths
        .map(({ column, colId, width }) => ({
          column,
          colId,
          fromWidth: width,
          toWidth: toWidthMap.get(colId),
        }))
        .filter(({ toWidth }) => typeof toWidth === 'number');

      if (animationColumns.length === 0) return;

      const hasVisibleChange = animationColumns.some(({ fromWidth, toWidth }) => Math.abs(fromWidth - toWidth) > 1);
      if (!hasVisibleChange || prefersReducedMotion()) {
        setDisplayedColumnWidths(
          gridApi,
          animationColumns.map(({ column, toWidth }) => ({ column, width: toWidth })),
          true,
        );
        return;
      }

      cancelColumnSizingAnimation();
      setColumnSizingAnimationState(true);

      const startTime = performance.now();
      const tick = (now) => {
        if (gridApi.isDestroyed?.()) {
          cancelColumnSizingAnimation();
          return;
        }

        const progress = Math.min(1, (now - startTime) / COLUMN_SIZING_ANIMATION_MS);
        const eased = easeInOutSine(progress);

        setDisplayedColumnWidths(
          gridApi,
          animationColumns.map(({ column, fromWidth, toWidth }) => ({
            column,
            width: fromWidth + (toWidth - fromWidth) * eased,
          })),
          progress >= 1,
        );

        if (progress < 1) {
          activeColumnSizingAnimationFrame = runOnNextFrame(tick);
          return;
        }

        activeColumnSizingAnimationFrame = null;
        setColumnSizingAnimationState(false);
      };

      activeColumnSizingAnimationFrame = runOnNextFrame(tick);
    };

    const scheduleAnimatedColumnSizing = debounce((gridApi, columnDefs) => {
      if (!gridApi || gridApi.isDestroyed?.()) return;
      if (!Array.isArray(columnDefs) || columnDefs.length === 0) return;
      cancelColumnSizingAnimation();
      if (scheduledColumnSizingFrame !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(scheduledColumnSizingFrame);
      }
      scheduledColumnSizingFrame = runOnNextFrame(() => {
        scheduledColumnSizingFrame = null;
        animateDynamicColumnSizing(gridApi, columnDefs);
      });
    }, COLUMN_SIZING_DEBOUNCE_MS);

    const scheduleDynamicColumnSizing = (gridApi, columnDefs) => {
      if (!gridApi || gridApi.isDestroyed?.()) return;
      if (!Array.isArray(columnDefs) || columnDefs.length === 0) return;
      if (!hasCompletedInitialColumnSizing) {
        const hasInitialRows = Array.isArray(rowData) && rowData.length > 0;
        if (hasInitialRows && !hasSeenFirstDataRendered) return;
        cancelColumnSizingAnimation();
        if (scheduledColumnSizingFrame !== null && typeof window.cancelAnimationFrame === 'function') {
          window.cancelAnimationFrame(scheduledColumnSizingFrame);
        }
        scheduledColumnSizingFrame = runOnNextFrame(() => {
          scheduledColumnSizingFrame = null;
          completeInitialColumnSizing(gridApi, columnDefs);
        });
        return;
      }
      scheduleAnimatedColumnSizing(gridApi, columnDefs);
    };

    const updateDataset = debounce(function (rows) {
       console.log("updating dataset called...")
        el.dataset.selectedRows = JSON.stringify(rows);
        if (el.dataset.agOnRowSelectedTarget !== undefined) {
          const target = document.querySelector(el.dataset.agOnRowSelectedTarget);
          if (target) {
            target.value = el.dataset.selectedRows;
            // Also update data-selected-rows attribute for consistency
            target.setAttribute('data-selected-rows', el.dataset.selectedRows);
          }
        }
    }, 200)

    function getRowNodeFromSelectionClick(event, gridApi) {
      const rowEl = event.target?.closest?.('.ag-row');
      if (!rowEl || !el.contains(rowEl)) return null;
      const rowIndex = rowEl.getAttribute('row-index');
      if (rowIndex !== null && rowIndex !== '') {
        const index = Number(rowIndex);
        if (Number.isInteger(index) && typeof gridApi.getDisplayedRowAtIndex === 'function') {
          return gridApi.getDisplayedRowAtIndex(index);
        }
      }
      const rowId = rowEl.getAttribute('row-id');
      if (rowId && typeof gridApi.getRowNode === 'function') {
        return gridApi.getRowNode(rowId);
      }
      return null;
    }

    function bindSelectionCheckboxWrapperClick(gridApi) {
      if (!gridApi || el._agSelectionCheckboxWrapperClickBound) return;
      el._agSelectionCheckboxWrapperClickBound = true;
      el.addEventListener('click', function (event) {
        const target = event.target;
        if (!target?.closest) return;
        if (target.closest('.ag-header-select-all')) return;
        const selectionEl = target.closest('.ag-selection-checkbox, .ag-checkbox-input-wrapper');
        if (!selectionEl || !el.contains(selectionEl)) return;

        const node = getRowNodeFromSelectionClick(event, gridApi);
        if (!node || node.selectable === false || typeof node.setSelected !== 'function') return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();

        node.setSelected(!node.isSelected(), false);
        selectedRows = gridApi.getSelectedRows?.() || [];
        updateDataset(selectedRows);
        updateDataset.flush?.();
      }, true);
    }

    function bindHeaderSelectAllCheckboxClick(gridApi) {
      if (!gridApi || el._agHeaderSelectAllClickBound) return;
      el._agHeaderSelectAllClickBound = true;
      el.addEventListener('click', function (event) {
        const target = event.target;
        if (!target?.closest) return;
        const headerSelectAll = target.closest('.ag-header-select-all');
        if (!headerSelectAll || !el.contains(headerSelectAll)) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();

        const headerCheckboxFilteredOnly = (gridOptions.columnDefs || []).some(
          colDef => colDef && colDef.headerCheckboxSelection && colDef.headerCheckboxSelectionFilteredOnly
        );
        const nodes = [];
        const collectNode = (node) => {
          if (!node || node.selectable === false) return;
          if (node.rowPinned || node.footer || node.group || node.level === -1) return;
          nodes.push(node);
        };

        if (headerCheckboxFilteredOnly && typeof gridApi.forEachNodeAfterFilterAndSort === 'function') {
          gridApi.forEachNodeAfterFilterAndSort(collectNode);
        } else if (typeof gridApi.forEachNode === 'function') {
          gridApi.forEachNode(collectNode);
        }

        const shouldSelect = nodes.some(node => !node.isSelected || !node.isSelected());
        if (typeof gridApi.setNodesSelected === 'function') {
          gridApi.setNodesSelected({ nodes, newValue: shouldSelect });
        } else {
          nodes.forEach(node => {
            if (typeof node.setSelected === 'function') node.setSelected(shouldSelect, false);
          });
        }

        selectedRows = gridApi.getSelectedRows?.() || [];
        updateDataset(selectedRows);
        updateDataset.flush?.();
      }, true);
    }

    /** Enable/disable grid toolbar comment + PDF (print) buttons for a report row id. */
    function wireReportGridCommentAndPrint(gridEl, rowId, rowData) {
      const gridContainer = gridEl.closest('[id$=":grid"]');
      const commentButton = gridContainer?.querySelector('[data-comment-button]');
      const printFormButton = gridContainer?.querySelector('[data-print-form-button]');
      const listUrl = gridEl.dataset.agListUrl;
      const hasId = rowId !== undefined && rowId !== null && rowId !== '';

      if (hasId) {
        gridEl.dataset.selectedRowId = rowId;
      } else {
        try {
          delete gridEl.dataset.selectedRowId;
        } catch (_e) {}
      }

      if (commentButton) {
        if (hasId) {
          commentButton.dataset.selectedRowId = rowId;
        } else {
          try {
            delete commentButton.dataset.selectedRowId;
          } catch (_e) {}
        }
        let formname = null;
        try {
          if (listUrl) {
            const parts = listUrl.split('/').filter(p => p);
            if (parts.length >= 3 && parts[0] === 'ssr') {
              formname = parts[1];
            } else if (parts.length >= 2) {
              formname = parts[parts.length - 2];
            }
          }
        } catch (_e) {}

        if (formname && hasId) {
          commentButton.setAttribute('hx-vals', JSON.stringify({
            key: rowId,
            formname: formname
          }));
          commentButton.disabled = false;
          commentButton.removeAttribute('disabled');
          try { commentButton.classList.remove('disabled', 'smaac-disabled'); } catch (_e) {}
          commentButton.title = 'View comment history';
          commentButton.offsetHeight;
        } else {
          commentButton.disabled = true;
          commentButton.title = 'Select a row to view comment history';
        }
      }

      if (printFormButton) {
        if (hasId) {
          var printId = (rowData && rowData.master_id) ? rowData.master_id : rowId;
          printFormButton.dataset.selectedRowId = printId;
          var hxVals = { id: printId };
          if (rowData && rowData.table_type) {
            printFormButton.dataset.selectedRowTableType = rowData.table_type;
            hxVals.table_type = rowData.table_type;
          } else {
            delete printFormButton.dataset.selectedRowTableType;
          }
          printFormButton.setAttribute('hx-vals', JSON.stringify(hxVals));
          printFormButton.disabled = false;
          printFormButton.removeAttribute('disabled');
          try { printFormButton.classList.remove('disabled', 'smaac-disabled'); } catch (_e) {}
          printFormButton.title = 'Print';
          printFormButton.offsetHeight;
        } else {
          try {
            delete printFormButton.dataset.selectedRowId;
            delete printFormButton.dataset.selectedRowTableType;
          } catch (_e) {}
          printFormButton.disabled = true;
          printFormButton.title = 'Select a row to print';
        }
      }
    }

    function selectReportRow(params, options = {}) {
      const runSelectionSideEffects = options.runSelectionSideEffects !== false;
      if (!params || !params.data || !params.api || !params.node) {
        return { rowIsSelectable: false, clickedRowId: null };
      }

      const rowIsSelectable = params.node.selectable !== false;
      const clickedRowId = params.data?.id;

      // Use AG Grid's exclusive row selection instead of deselectAll()+refresh.
      // The old path caused extra DOM churn during rapid clicks and made double-click
      // detection unreliable on report grids.
      el._suppressSelectionChanged = true;
      try {
        if (rowIsSelectable) {
          params.node.setSelected(true, true);
        } else {
          params.api.deselectAll();
        }
      } finally {
        el._suppressSelectionChanged = false;
      }

      selectedRows = params.api.getSelectedRows() || [];
      updateDataset(selectedRows);

      const hasSelectOrdersToApprove = rowIsSelectable
        && el.dataset.usesApprovalHandler === 'true'
        && typeof window.selectOrdersToApprove === 'function';

      if (runSelectionSideEffects && hasSelectOrdersToApprove) {
        try {
          window.selectOrdersToApprove(el);
        } catch (e) {
          console.error('Failed to call selectOrdersToApprove:', e);
        }
      }

      if (clickedRowId !== undefined) {
        wireReportGridCommentAndPrint(el, clickedRowId, params.data);
      }

      return { rowIsSelectable, clickedRowId };
    }

    function getRowIdentifier(rowData) {
        // Try different possible row identifiers in order of preference
        // NOTE: A stable row id is critical for correct multi-select behavior
        // (especially header "select all", sorting, filtering, and data refresh).
        const possibleIds = [
            "id",
            "link_id",
            "pk",
            "uuid",
            "guid",
            "ID",
            "Id",
            "awsfile_id",
        ];
        
        for (const idField of possibleIds) {
            if (rowData.hasOwnProperty(idField) && rowData[idField] != null) {
                return { field: idField, value: rowData[idField] };
            }
        }
        
        // Fallback to using the entire row if no ID found
        return { field: null, value: JSON.stringify(rowData) };
    }

    function onRowSelected(event) {
        if (el._suppressSelectionChanged) return;
        const selectedRowData = event.node.data;
        const isSelected = event.node.isSelected();

        const rowId = getRowIdentifier(selectedRowData);
        
        let existingIndex = -1;
        if (rowId.field) {
            existingIndex = selectedRows.findIndex(row => row[rowId.field] === selectedRowData[rowId.field]);
        } else {
            existingIndex = selectedRows.findIndex(row => JSON.stringify(row) === JSON.stringify(selectedRowData));
        }

        if (isSelected) {
            if (existingIndex === -1) {
                selectedRows.push(selectedRowData);
            }
        } else {
            if (existingIndex !== -1) {
                selectedRows.splice(existingIndex, 1);
            }
        }


        updateDataset(selectedRows);
        return selectedRows;
    }

    function onCellValueChanged(event) {
        const rowData = event.data;
        const rowId = getRowIdentifier(rowData);
        
        let existingIndex = -1;
        if (rowId.field) {
            existingIndex = selectedRows.findIndex(row => row[rowId.field] === rowData[rowId.field]);
        } else {
            existingIndex = selectedRows.findIndex(row => JSON.stringify(row) === JSON.stringify(rowData));
        }
        
        if (existingIndex !== -1) {
            selectedRows[existingIndex] = rowData;
        } else {
            selectedRows.push(rowData);
        }
        updateDataset(selectedRows);
    }
    function onFirstDataRendered(params) {
        //autoSizeAll(false);
    }
    const baseOnGridReady = (params) => {
      // Initial sizing
      scheduleDynamicColumnSizing(params.api, gridOptions.columnDefs);
      if (gridOptions.disablePersistentGridState) {
        clearStoredGridState(el.dataset.agId, { includeNavigationState: false });
      }

      // Try restoring saved state from a previous dataform navigation
      _savedScrollRow = tryRestoreGridState(params.api, el.dataset.agId);

      if (_savedScrollRow === null && !gridOptions.disablePersistentGridState) {
        _savedScrollRow = restorePersistentGridState(params.api, el.dataset.agId);
      }

      // Restored nav/persisted snapshots no longer carry a filter (getGridStateSnapshot
      // stores filterModel:{}), so the saved server-side template filter must still be
      // applied whenever the grid has none — else a saved template filter vanishes after
      // entering a record and pressing Exit (#7785), since scroll/column restore alone
      // used to suppress it.
      const restoredFilter = params.api.getFilterModel();
      if (!restoredFilter || Object.keys(restoredFilter).length === 0) {
        let filterModel = agGridOptions["filter"]
        if(!filterModel) filterModel = {}
        Object.keys(filterModel).forEach(key => {
          if(!filterModel[key]) delete filterModel[key]
        })
        params.api.setFilterModel(filterModel);
        if(Object.keys(filterModel).length === 0) params.api.onFilterChanged();
      } else {
        // Trigger filter changed so UI indicators (e.g. amber highlight) update
        params.api.onFilterChanged();
      }

      params.api.getGridElement = () => el;

      // Setup tab navigation
      window.setupTabNavigation(params.api);

      prevGroupedColIds = params.api
        .getColumnState()
        .filter(c => c.rowGroup)
        .map(c => c.colId);
    };

    const baseOnGridSizeChanged = (params) => {
      scheduleDynamicColumnSizing(params.api, gridOptions.columnDefs);
    };

    const baseOnFirstDataRendered = (params) => {
      hasSeenFirstDataRendered = true;
      scheduleDynamicColumnSizing(params.api, gridOptions.columnDefs);
      htmx.process(el); // Process HTMX attributes
      ag.reinitializeHtmx(); // Ensure all attachment buttons are processed

      // Restore scroll position if returning from dataform
      if (_savedScrollRow !== null && _savedScrollRow > 0) {
        try {
          params.api.ensureIndexVisible(_savedScrollRow, 'top');
        } catch (_e) {}
        _savedScrollRow = null;
      }

      // Also try setting up tab navigation here in case onGridReady timing doesn't work
      if (window.setupTabNavigation) {
        window.setupTabNavigation(params.api);
      }
    };

    const baseOnRowDataUpdated = (params) => {
      scheduleDynamicColumnSizing(params.api, gridOptions.columnDefs);
      htmx.process(el); // Re-process HTMX attributes if row data changes
      ag.reinitializeHtmx(); // Re-process attachment buttons after data updates
    };

    class SearchableSelectEditor {
      init(params) {
        this.params = params;
        this.isMultiSelect = !!params.multiSelect;
        this.value = Array.isArray(params.value) ? params.value : [params.value].filter(Boolean);
        this.options = [...new Set([...(params.values || []), ...this.value])];
        this.selectedIndex = -1;
        this.isCustomValue = false;
        this.searchDebounced = debounce(this.onSearch.bind(this), 300);

        /* container -------------------------------------------------- */
        this.container = document.createElement('div');
        this.container.className = 'ag-cell-editor-container';
        Object.assign(this.container.style, {
          display: 'flex',
          flexDirection: 'column',
          width: `${params.column.getActualWidth()}px`,
          maxWidth: `${params.column.getActualWidth()}px`,
          maxHeight: '200px',
          overflow: 'hidden',
          marginTop: '3px',
        });

        /* search box -------------------------------------------------- */
        this.searchInput = document.createElement('input');
        this.searchInput.className = 'search-input';
        this.searchInput.type = 'text';
        this.searchInput.placeholder = this.isMultiSelect
          ? 'Search / add… (⏎ to commit)'
          : 'Search or type new value…';
        this.searchInput.value = this.value[0] || '';
        this.container.appendChild(this.searchInput);

        /* select list container -------------------------------------- */
        this.selectContainer = document.createElement('div');
        Object.assign(this.selectContainer.style, {
          width: '100%',
          marginTop: '2px',
          maxHeight: 'calc(50vh - 50px)',
          overflowY: 'auto',
          border: '1px solid #ccc',
          borderRadius: '4px',
          backgroundColor: '#fff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        });
        this.container.appendChild(this.selectContainer);

        /* style ------------------------------------------------------ */
        const style = document.createElement('style');
        style.textContent = `
          .ag-cell-editor-container .option-item {
            display: flex;
            align-items: center;
            padding: 8px;
            cursor: pointer;
            border-bottom: 1px solid #eee;
          }
          .ag-cell-editor-container .option-item:hover {
            background-color: #f5f5f5;
          }
          .ag-cell-editor-container .option-item.selected {
            background-color: #e3f2fd;
          }
          .ag-cell-editor-container .option-item input[type="checkbox"] {
            margin-right: 8px;
          }
          .ag-cell-editor-container .search-input {
            padding: 8px;
            border: 1px solid #ccc;
            border-radius: 4px;
            width: 100%;
            box-sizing: border-box;
          }
          .ag-cell-editor-container .no-results {
            padding: 8px;
            color: #666;
            font-style: italic;
          }
        `;
        document.head.appendChild(style);

        /* populate & pre-select -------------------------------------- */
        this.populateOptions(this.options);
        this.updateSelectedValues();

        /* listeners --------------------------------------------------- */
        this.searchInput.addEventListener('input', () => {
          // If the search input is empty, clear the value array
          if (!this.searchInput.value.trim()) {
            this.selectedIndex = -1;            // clear keyboard pointer
            this.updateSelectedValues();
            this.value = [];
          }
          this.searchDebounced();
        });
        this.searchInput.addEventListener('keydown', e => this.handleKeyDown(e));
        this.selectContainer.addEventListener('mousedown', e => e.preventDefault()); // prevent blur on searchInput when clicking an option
        this.selectContainer.addEventListener('click', e => this.handleOptionClick(e));

        // Add blur handler for manual entry
        this.searchInput.addEventListener('blur', () => {
          if (this.searchInput.value && !this.options.includes(this.searchInput.value)) {
            this.isCustomValue = true;
            const newValue = this.searchInput.value;
            // Add to options list
            this.options.push(newValue);
            if (this.isMultiSelect) {
              this.value.push(newValue);
            } else {
              this.value = [newValue];
            }
            // Refresh the dropdown with the new option
            this.populateOptions(this.options);
            this.updateSelectedValues();
          }
        });
      }

      handleOptionClick(e) {
        const optionItem = e.target.closest('.option-item');
        if (!optionItem) return;

        const value = optionItem.dataset.value;
        const checkbox = optionItem.querySelector('input[type="checkbox"]');
        
        if (this.isMultiSelect) {
          checkbox.checked = !checkbox.checked;
          if (checkbox.checked) {
            this.value.push(value);
          } else {
            const index = this.value.indexOf(value);
            if (index > -1) {
              this.value.splice(index, 1);
            }
          }
        } else {
          this.value = [value];
          this.searchInput.value = value;
          this.params.stopEditing?.();
        }
        
        this.updateSelectedValues();
      }

      updateSelectedValues() {
        const options = this.selectContainer.querySelectorAll('.option-item');
        options.forEach(option => {
          const value = option.dataset.value;
          const checkbox = option.querySelector('input[type="checkbox"]');
          if (this.isMultiSelect) {
            checkbox.checked = this.value.includes(value);
            option.classList.toggle('selected', this.value.includes(value));
          } else {
            option.classList.toggle('selected', this.value[0] === value);
          }
        });
      }

      handleKeyDown(e) {
        if (this.isMultiSelect && e.key === 'Enter') {
          e.preventDefault();
          if (this.searchInput.value && !this.options.includes(this.searchInput.value)) {
            const newValue = this.searchInput.value;
            // Add to options list
            this.options.push(newValue);
            this.value.push(newValue);
            // Refresh the dropdown with the new option
            this.populateOptions(this.options);
            this.updateSelectedValues();
          }
          this.params.stopEditing?.();
          return;
        }

        const options = Array.from(this.selectContainer.querySelectorAll('.option-item'));
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            this.selectedIndex = Math.min(this.selectedIndex + 1, options.length - 1);
            this.updateSelection();
            break;
          case 'ArrowUp':
            e.preventDefault();
            this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
            this.updateSelection();
            break;
          case 'Enter':
            e.preventDefault();
            if (this.selectedIndex >= 0) {
              const option = options[this.selectedIndex];
              option.click();
            } else if (this.searchInput.value && !this.options.includes(this.searchInput.value)) {
              const newValue = this.searchInput.value;
              // Add to options list
              this.options.push(newValue);
              if (this.isMultiSelect) {
                this.value.push(newValue);
              } else {
                this.value = [newValue];
              }
              // Refresh the dropdown with the new option
              this.populateOptions(this.options);
              this.updateSelectedValues();
              this.params.stopEditing?.();
            }
            break;
          case 'Escape':
            e.preventDefault();
            this.params.stopEditing?.();
            break;
        }
      }

      updateSelection() {
        const options = Array.from(this.selectContainer.querySelectorAll('.option-item'));
        if (this.selectedIndex >= 0) {
          options.forEach((opt, idx) => {
            opt.classList.toggle('selected', idx === this.selectedIndex);
          });
          options[this.selectedIndex].scrollIntoView({ block: 'nearest' });
        }
      }

      populateOptions(options) {
        this.selectContainer.innerHTML = '';
        if (!options.length) {
          const noRes = document.createElement('div');
          noRes.className = 'no-results';
          noRes.textContent = 'No results found. Type to add new value.';
          this.selectContainer.appendChild(noRes);
          return;
        }

        options.forEach(opt => {
          const optionItem = document.createElement('div');
          optionItem.className = 'option-item';
          optionItem.dataset.value = opt;

          if (this.isMultiSelect) {
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.checked = this.value.includes(opt);
            optionItem.appendChild(checkbox);
          }

          const label = document.createElement('span');
          label.textContent = opt;
          optionItem.appendChild(label);

          if (this.value.includes(opt)) {
            optionItem.classList.add('selected');
          }

          this.selectContainer.appendChild(optionItem);
        });
      }

      onSearch() {
        const term = this.searchInput.value.toLowerCase();
        const filtered = this.options.filter(o => o.toString().toLowerCase().includes(term));
        this.populateOptions(filtered);
        this.updateSelectedValues();
      }

      getGui() { return this.container; }
      afterGuiAttached() {
        // Ensure focus reliably lands in the search box when the editor opens
        try {
        this.searchInput.focus();
        this.searchInput.select();
          // Defer once to override any focus taken back by the grid during Tab navigation
          setTimeout(() => {
            this.searchInput.focus();
            this.searchInput.select();
          }, 0);
        } catch (e) {
          // no-op safeguard
        }
      }
      isPopup() { return true; }
      destroy() {}
      isCancelBeforeStart() { return false; }
      isCancelAfterEnd() { return false; }

      getValue() {
        if (this.isMultiSelect) {
          return this.value;
        }
        // Return null if the input is empty or contains only whitespace
        return (this.value[0] || this.searchInput.value.trim()) ? (this.value[0] || this.searchInput.value) : null;
      }
    }
      // Initial rows come from SSR as JSON script payloads. Older partials may
      // still provide base64(JSON.stringify([...])) in data-ag-initial-rows.
      // In rare cases, some pages may accidentally pass a wrapped object; normalize.
      const rowData = coerceRowArray(parseGridJsonPayload(
        el?.dataset?.agInitialRowsScript,
        el?.dataset?.agInitialRows,
        []
      ));
      
    let prevGroupedColIds = [];
    let isApplyingGroupLimit = false;      

    const APPROVAL_SELECTABLE_STATUSES = new Set([
      'submitted',
      'approved1',
      'approved2',
      'approved3',
      '1st approved',
      '2nd approved',
      '3rd approved',
      'first approved',
      'second approved',
      'third approved',
      'hr approved',
      'marketing approved',
      'plant approved',
      'design approved',
      'project approved',
    ]);

    const normalizeApprovalStatus = (value) => {
      return String(value ?? '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim()
        .toLowerCase()
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ');
    };

    const isApprovalSelectableRow = (rowData) => {
      if (!rowData) return false;
      const status = normalizeApprovalStatus(rowData.status ?? rowData.Status);
      return APPROVAL_SELECTABLE_STATUSES.has(status);
    };

    const defaultIsRowSelectable = (rowNode) => {
      if (!rowNode || rowNode.rowPinned || rowNode.level === -1 || rowNode.footer) return false;
      if (rowNode.data && rowNode.data.Production_Done && rowNode.data.Production_Done === 'True') {
        return false;
      }
      if (rowNode.data && rowNode.data.MRN_Done && rowNode.data.MRN_Done === 'True') {
        return false;
      }
      if (rowNode.data && rowNode.data.qc_check && rowNode.data.qc_check === 'False') {
        return false;
      }
      if (
        rowNode.data
        && rowNode.data.Production_QC_Pending
        && rowNode.data.Production_QC_Pending === 'True'
      ) {
        return false;
      }
      if (rowNode.data && rowNode.data.Shearing_Done && rowNode.data.Shearing_Done === 'False') {
        return false;
      }
      if (rowNode.data && rowNode.data.Stage_QC_Done && rowNode.data.Stage_QC_Done === 'True') {
        return false;
      }
      if (
        !gridOptions.allowDispatchDoneSelection
        && rowNode.data
        && rowNode.data.Dispatch_done
        && rowNode.data.Dispatch_done === 'True'
      ) {
        return false;
      }
      if (rowNode.data && rowNode.data.PreviousProdNotDone && rowNode.data.PreviousProdNotDone === 'True') {
        return false;
      }
      if (rowNode.data && rowNode.data.Contractor_billing_done && rowNode.data.Contractor_billing_done === 'True') {
        return false;
      }
      if (rowNode.data && rowNode.data.jobcard && rowNode.data.jobcard === 'True') {
        return false;
      }
      if (rowNode.data && rowNode.data.packinglist && rowNode.data.packinglist === 'True') {
        return false;
      }
      if (rowNode.data && rowNode.data.sticker && rowNode.data.sticker === 'True') {
        return false;
      }
      if (rowNode.data && rowNode.data.next_stage_check && rowNode.data.next_stage_check === 'False') {
        return false;
      }
      if (rowNode.data && rowNode.data.deleteDisabled && rowNode.data.deleteDisabled === 'True') {
        return false;
      }
      return true;
    };

    var gridOptions = {
      columnDefs: [],
      rowData: rowData,
      // onFirstDataRendered: onFirstDataRendered,
      onGridReady: baseOnGridReady,
      onColumnRowGroupChanged: (params) => {
        if (isApplyingGroupLimit) return;
      
        const api = params.api;
        if (!api || !api.getColumnState) return;
      
        const columnState = api.getColumnState();
        const groupedCols = columnState.filter(c => c.rowGroup);
        const groupedIds = groupedCols.map(c => c.colId);
      
        const newlyAdded = groupedIds.find(
          id => !prevGroupedColIds.includes(id)
        );
      
        if (groupedCols.length > 4 && newlyAdded) {
          isApplyingGroupLimit = true;
      
          api.applyColumnState({
            state: [{ colId: newlyAdded, rowGroup: false }],
            applyOrder: false,
          });
      
          showFlashMessage(
            null,
            'warning',
            'Maximum 4 group columns are allowed',
            5
          );
      
          isApplyingGroupLimit = false;
          return;
        }
      
        const limitReached = groupedCols.length >= 4;
      
        isApplyingGroupLimit = true;
      
        api.applyColumnState({
          state: columnState.map(col => ({
            colId: col.colId,
            enableRowGroup: !limitReached || col.rowGroup === true,
          })),
          applyOrder: false,
        });
      
        isApplyingGroupLimit = false;
      
        prevGroupedColIds = groupedIds;
      },      
      onGridSizeChanged: baseOnGridSizeChanged,
      getRowStyle: (params) => {
        // Make totals rows (group footer + grand total + pinned totals) bold.
        // This matches the "totals" look across grids.
        const n = params && params.node;
        const isTotalsRow = !!(n && (n.footer || n.level === -1 || n.rowPinned));
        if (isTotalsRow) {
          return { "z-index": 1, "font-weight": "700" };
        }
      },

      rowSelection: 'multiple',
      headerHeight: window.innerWidth <= 768 ? 35 : 27,
      floatingFiltersHeight: 20,
      rowHeight: window.innerWidth <= 768 ? 40 : 20,
      suppressRowClickSelection: true,
      onRowSelected: onRowSelected,
      onSelectionChanged: (event) => {
        if (el._suppressSelectionChanged) return;
        try {
          selectedRows = event.api.getSelectedRows() || [];
          updateDataset(selectedRows);
        } catch (_e) {}
      },
      // onCellValueChanged: onCellValueChanged,
      enableRangeSelection: true,
      animateRows: true,
      onFirstDataRendered: baseOnFirstDataRendered,
      onRowDataUpdated: baseOnRowDataUpdated,
      autoGroupColumnDef: {
        minWidth: 300,
      },
      rowClassRules: {
        'balqtyzero-highlight-row': function (params) {
          if (params.data && params.data.IsBalanceQtyZero && params.data.IsBalanceQtyZero === 1) {
            return true;
          }
          return false;
        },
        'qc-highlight-row': function (params) {
          if (params.data && params.data.qc_check && params.data.qc_check === 'False') {
            return true;
          }
          if (params.data && params.data.jobcard && params.data.jobcard === 'True') {
            return true;
          }
          if (params.data && params.data.Production_Done && params.data.Production_Done === 'True') {
            return true;
          }
          return false;
        },
        'production-qc-pending-highlight-row': function (params) {
          if (
            params.data
            && params.data.Production_QC_Pending
            && params.data.Production_QC_Pending === 'True'
          ) {
            return true;
          }
          return false;
        },
        'mrn-highlight-row': function (params) {
          if (params.data && params.data.MRN_Done && params.data.MRN_Done === 'True') {
            return true;
          }
          return false;
        },
        'shearing-not-done-highlight-row': function (params) {
          if (params.data && params.data.Shearing_Done && params.data.Shearing_Done === 'False') {
            return true;
          }
          return false;
        },
        'qc-already-highlight-row': function (params) {
          if (params.data && params.data.Stage_QC_Done && params.data.Stage_QC_Done === 'True') {
            return true;
          }
          if (params.data && params.data.sticker && params.data.sticker === 'True') {
            return true;
          }
          return false;
        },
        'disp-highlight-row': function (params) {
          if (params.data && params.data.Dispatch_done && params.data.Dispatch_done === 'True') {
            return true;
          }
          if (params.data && params.data.packinglist && params.data.packinglist === 'True') {
            return true;
          }
          if (params.data && params.data.Loaded === 'True') {
            return true;
          }
          return false;
        },
        'contractor-billing-highlight-row': function (params) {
          if (params.data && params.data.Contractor_billing_done && params.data.Contractor_billing_done === 'True') {
            return true;
          }
          return false;
        },
        'multiple-highlight-row': function (params) {
          if (params.data && params.data.Multiple === 'True') {
            return true;
          }
          return false;
        },
        'stage-highlight-row': function (params) {
          if (params.data && params.data.next_stage_check && params.data.next_stage_check === 'False') {
            return true;
          }
          return false;
        },
        'previous-prod-not-done-highlight-row': function (params) {
          if (params.data && params.data.PreviousProdNotDone && params.data.PreviousProdNotDone === 'True') {
            return true;
          }
          return false;
        },
        'hide-checkbox': function (params) {
          if (
            params.data && params.data.deleteDisabled
          ){
            console.log("params: checkbox", params);
          }
          if (params.data && params.data.deleteDisabled && params.data.deleteDisabled === 'True') {
            return true;
          }
          return false;
        },
        'footer-row': (params) => params.node.rowPinned === 'bottom',
      },
      isRowSelectable: defaultIsRowSelectable,
      onCellClicked: params => {
        if (params.data === undefined || params.colDef.noActionOnClicked || params.colDef.field?.awsfile_count) { 
            return;
        }
        // Ignore clicks anywhere in the selection checkbox cell; AG Grid handles selection natively.
        if (isSelectionCheckboxCellClick(params)) {
            return;
        }
        // For report grids, single click selects the row instead of opening
        if (gridOptions && (gridOptions.grid_type === 'report' || gridOptions.grid_type === 'groupreport')) {
            // Ctrl+Click or Cmd+Click: open dataform in a new tab
            if (params.event && (params.event.ctrlKey || params.event.metaKey) && el.dataset.landingUrl) {
              openGridRecordInNewTab(el.dataset.landingUrl, params.data);
              return;
            }
            const isDoubleClickSequence = Number(params.event?.detail || 1) > 1;
            selectReportRow(params, {
              runSelectionSideEffects: !isDoubleClickSequence,
            });
            return;
        }
        
        if (gridOptions && gridOptions.grid_type === 'groupreport') {
            // Skip navigation for groupreport grids
        } else if (!params.column.isCellEditable(params) && params.colDef.field !== 'awsfile_count') {
            let searchParams = new URLSearchParams();
            searchParams.append('showview', 'dataform');
            
            for (var key in params.data) {
                if (params.data.hasOwnProperty(key) && !key.includes('date')) {
                    // This will ensure only one value per key
                    searchParams.set(key, params.data[key]);
                }
            }

            if (preserveGridNavContext(searchParams, params)) return;

            htmx.ajax('POST', el.dataset.landingUrl, {
                target: `#${el.dataset.agId}`,
                values: Object.fromEntries(searchParams)
            });
        }


        if (params.colDef.dataType === "iframe") {
            let href = params.data[`${params.colDef.field}.iframe`];
            if (href) {
                document.getElementById('AgIFrame').src = href;
                window.openIframeModal('AgIFrame-modal',href);
            }
            return;
        }
      },
      onRowDoubleClicked: params => {
        // Allow disabling "double-click opens record" per-grid.
        // Default behavior is enabled unless explicitly set to false.
        if (gridOptions && gridOptions.doubleClickOpenRecord === false) {
            return;
        }
        // For report grids, double click opens the record
        if (params.data === undefined) { 
            return;
        }
        if (gridOptions && (gridOptions.grid_type === 'report' || gridOptions.grid_type === 'groupreport')) {
            selectReportRow(params, { runSelectionSideEffects: false });
            let searchParams = new URLSearchParams();
            searchParams.append('showview', 'dataform');
            
            for (var key in params.data) {
                if (params.data.hasOwnProperty(key) && !key.includes('date')) {
                    searchParams.set(key, params.data[key]);
                }
            }

            if (preserveGridNavContext(searchParams, params)) return;

            // Push current front report URL to history before navigating to dataform
            // This ensures browser back button goes to front report, not dashboard
            history.pushState({}, '', window.location.href);

            htmx.ajax('POST', el.dataset.landingUrl, {
                target: `#${el.dataset.agId}`,
                values: Object.fromEntries(searchParams),
                pushUrl: true
            });
        }
      },
      components: {
        headerInfoComponent: HeaderInfoComponent,
        searchableSelectEditor: SearchableSelectEditor,
      },
      stopEditingWhenCellsLoseFocus: true,
      excelExport: true,
      // Backend sends numbers as comma-formatted strings ("1,06,247"), which Excel
      // imports as text and refuses to sum. Export numeric columns as real numbers.
      defaultExcelExportParams: {
        processCellCallback: (params) => {
          const value = params.value;
          const colDef = params.column && params.column.getColDef && params.column.getColDef();
          if (colDef && colDef.__exportAsNumber && value != null && value !== '') {
            const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
            if (!isNaN(num)) return num;
          }
          return value;
        },
      },
      groupDefaultExpanded: -1,
      groupTotalRow: "bottom",
      grandTotalRow: "bottom",
      rowGroupPanelShow: "always",
      groupDisplayType: 'multipleColumns',
      defaultColDef: {
        filter: 'agMultiColumnFilter',
        filterParams: {
          // Multi filter = Text filter (top) + Set filter (bottom)
          filters: [
            {
              filter: 'agTextColumnFilter',
              filterParams: {
                caseSensitive: false,
                trimInput: false,
                filterOptions: [
                  'contains',
                  AG_GRID_CLEAR_FILTER_OPTION,
                  'notContains',
                  'equals',
                  'notEqual',
                  'startsWith',
                  'endsWith',
                  'blank',
                  'notBlank',
                  {
                    displayKey: 'multipleValues',
                    displayName: 'Multiple Values',
                    numberOfInputs: 1,
                    predicate: (filterValues, cellValue) => {
                      const raw = Array.isArray(filterValues) ? filterValues[0] : filterValues;
                      const tokens = splitMultiValuesInput(raw).map(normalizeMultiValueToken).filter(Boolean);
                      if (tokens.length === 0) return true; // behave like "no filter"

                      if (cellValue === null || cellValue === undefined) return false;
                      const cellStr = String(cellValue).trim();
                      if (!cellStr) return false;

                      // Exact match (prevents CL1 matching ECL1).
                      // If cell contains multiple tokens (e.g. "CL1 CL2"), match any token exactly.
                      const cellTokens = splitMultiValuesInput(cellStr).map(normalizeMultiValueToken).filter(Boolean);
                      if (cellTokens.length > 1) {
                        return cellTokens.some(ct => tokens.includes(ct));
                      }
                      const cellNorm = normalizeMultiValueToken(cellStr);
                      return tokens.includes(cellNorm);
                    },
                  },
                ],
              },
            },
            {
              filter: 'agSetColumnFilter',
              filterParams: {
                caseSensitive: false,
                applyMiniFilterWhileTyping: true,
              },
            },
          ],
        },
        sortable: true,
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        enableRowGroup: true,
        singleClickEdit: true,
        suppressNavigable: false, // Allow keyboard navigation
        aggFunc: (params) => {
          const colDef = params?.column?.getColDef ? params.column.getColDef() : null;
          if (colDef && (colDef.noTotals === true || colDef.noTotals === 'true')) {
            return null;
          }
          // IMPORTANT: existing vouchers often load numeric cells as strings (sometimes with commas).
          // Coerce numeric-like values so grandTotalRow/groupTotalRow sum correctly.
          const toNum = (val) => {
            if (val === null || val === undefined) return 0;
            if (typeof val === 'number') return isNaN(val) ? 0 : val;
            if (typeof val === 'string') {
              const s = val.replace(/,/g, '').trim();
              if (!s) return 0;
              const n = Number(s);
              return isNaN(n) ? 0 : n;
            }
            const n = Number(val);
            return isNaN(n) ? 0 : n;
          };
          const sum = (params.values || []).reduce((acc, val) => acc + toNum(val), 0);
          if (isNaN(sum) || sum === 0) return "";
          return sum % 1 === 0 ? sum : parseFloat(sum.toFixed(2));
        },
        cellClassRules: {
            'no-checkbox': function(params) {
              return params.data === undefined && params.node.level === -1;
            },
            'hide-checkbox': function(params) {
              if (
                params.data && params.data.deleteDisabled
              ){
                console.log("params: checkbox", params);
              }
              if (params.data && params.data.deleteDisabled && params.data.deleteDisabled === 'True') {
                return true;
              }
              return false;
            },
            'editable-cell': isAgGridEditableCell,
            'editable-required-cell': isAgGridRequiredCell,
            'editable-optional-cell': params => isAgGridEditableCell(params) && !isAgGridRequiredCell(params),
            'hide-footer-checkbox': params => params.node.level === -1,
        }
      },
    };
    gridOptions = {
      ...gridOptions,
      ...agGridOptions
    };
    ['grandTotalRow', 'groupTotalRow'].forEach((key) => {
      if (gridOptions.suppressTotalRows || gridOptions[key] === null || gridOptions[key] === false || gridOptions[key] === 'none') {
        delete gridOptions[key];
      }
    });
    applySelectionSummaryDefaults(gridOptions);
    if (!gridOptions.popupParent) {
      gridOptions.popupParent = document.body;
    }
    gridOptions.localeText = {
      ...(gridOptions.localeText || {}),
      empty: 'Clear Filter',    };
    gridOptions.rowClassRules = {
      ...(gridOptions.rowClassRules || {}),
      'smaac-ag-row-manual-height': params => params.node?.__smaacManualRowHeight === true,
    };

    // -----------------------------------------------------------------------
    // Linked entry: clicking a row in one grid refreshes another grid's list
    // -----------------------------------------------------------------------
    function attachLinkedEntryRowClick() {
      const cfg = gridOptions && (gridOptions.rowClickListRefresh || gridOptions.linkedEntry);
      const isLinkedType = gridOptions && gridOptions.grid_type === 'linkedentry';
      if (!cfg && !isLinkedType) return;

      const targetGridId = (cfg && (cfg.targetGridId || cfg.target)) || null;
      const sourceField = (cfg && (cfg.sourceField || cfg.field)) || 'itemcode';
      const targetParam = (cfg && (cfg.targetParam || cfg.param)) || sourceField;
      const extraParams = (cfg && cfg.extraParams) || null; // object of {param: value|{fromRow:"field"}}
      const clearOnEmpty = cfg && cfg.clearOnEmpty === false ? false : true;

      if (!targetGridId) {
        console.warn('linkedentry: missing targetGridId');
        return;
      }

      const doRefresh = (rowData) => {
        const val = rowData ? rowData[sourceField] : null;
        const hasVal = !(val === null || val === undefined || String(val).trim() === '' || String(val) === '0');

        const targetEl = document.getElementById(targetGridId);
        const targetApi = window[`aggridApi:${targetGridId}`];
        if (!targetEl || !targetApi) {
          console.warn('linkedentry: target grid not found/initialized', targetGridId);
          return;
        }

        if (!hasVal) {
          if (clearOnEmpty) {
            try { targetApi.setGridOption('rowData', []); } catch (_e) {}
          }
          return;
        }

        // Prefer using the target grid's refresh button URL (includes SSR params)
        let urlStr = null;
        try {
          const container = document.getElementById(`${targetGridId}:grid`);
          const refreshBtn = container ? container.querySelector('#refreshButton') : null;
          urlStr = refreshBtn && refreshBtn.dataset ? refreshBtn.dataset.listUrl : null;
        } catch (_e) {}
        if (!urlStr) {
          urlStr = targetEl.dataset.agListUrl || '';
        }
        if (!urlStr) {
          console.warn('linkedentry: target grid has no list url', targetGridId);
          return;
        }

        const u = new URL(urlStr, window.location.origin);
        u.searchParams.set(targetParam, String(val));

        // Apply optional extra params
        if (extraParams && typeof extraParams === 'object') {
          Object.keys(extraParams).forEach((k) => {
            const v = extraParams[k];
            if (v && typeof v === 'object' && v.fromRow) {
              u.searchParams.set(k, String((rowData && rowData[v.fromRow]) ?? ''));
            } else if (v !== undefined && v !== null) {
              u.searchParams.set(k, String(v));
            }
          });
        }

        try { targetApi.setGridOption?.('loading', true); } catch (_e) {}
        fetchWithError(u.toString(), { method: 'GET' }, 'Unable to refresh linked grid.')
          .then((r) => r.json())
          .then((res) => {
            const rows = res && res.get_list && Array.isArray(res.get_list.result) ? res.get_list.result : [];
            targetApi.setGridOption('rowData', rows);
          })
          .catch((e) => {
            console.error('linkedentry: failed to refresh target grid', e);
            try { targetApi.setGridOption('rowData', []); } catch (_e2) {}
          })
          .finally(() => {
            try { targetApi.setGridOption?.('loading', false); } catch (_e) {}
          });
      };

      const existing = gridOptions.onRowClicked;
      gridOptions.onRowClicked = (params) => {
        try {
          if (typeof existing === 'function') existing(params);
        } catch (_e) {}
        if (!params || !params.data) return;
        doRefresh(params.data);
      };
    }

    attachLinkedEntryRowClick();

    // If excelExport is explicitly disabled in grid options,
    // remove export items from the context menu.
    if (gridOptions.excelExport === false) {
      const existingGetContextMenuItems = gridOptions.getContextMenuItems;
      gridOptions.getContextMenuItems = function (params) {
        let items = [];
        if (typeof existingGetContextMenuItems === 'function') {
          items = existingGetContextMenuItems(params) || [];
        } else if (Array.isArray(params.defaultItems)) {
          items = params.defaultItems.slice();
        }
        const exportKeys = new Set(['export', 'exportCsv', 'exportExcel']);
        return items.filter((item) => {
          const key = typeof item === 'string' ? item : item?.name || item?.action;
          return !exportKeys.has(key);
        });
      };
    }
    let gridTheme = null;
    if(el.dataset.gridTemplateTheme && themes[el.dataset.gridTemplateTheme]){
      gridTheme = themes[el.dataset.gridTemplateTheme];
      if(el.dataset.gridTemplateColorScheme && colorScheme[el.dataset.gridTemplateColorScheme]){
        gridTheme = gridTheme.withPart(colorScheme[el.dataset.gridTemplateColorScheme]);
        console.log("Applied Color Scheme: ", el.dataset.gridTemplateColorScheme)
      }
    }
    console.log("Final Theme: ", gridTheme)
    if(gridTheme){
      gridOptions.theme = gridTheme;
    }

    var list_url = el.dataset.agListUrl;
    if (window.ag.attachCellRenderFunctions) {
      window.ag.attachCellRenderFunctions(gridOptions.columnDefs);
    };

    gridOptions.onViewportChanged = () => ag.reinitializeHtmx();
    
    // Simple modal handling
    document.addEventListener('htmx:afterSwap', function(event) {
      if (event.target && event.target.id === 'attachmentModal') {
        ag.reinitializeHtmx();
        setTimeout(function() {
          syncAwsAttachmentCountsForApi(window['aggridApi:' + el.dataset.agId]);
        }, 0);
      }
      
      // If we just navigated from a grid entry (opened dataform), replace current history
      // Check if we're on a dataform page (showview=dataform) and have stored landing URL
      if (window._gridEntryLandingUrl && event.target) {
        // Check if the target contains dataform indicators
        const isDataform = event.target.querySelector('[data-formname]') || 
                          event.target.closest('[data-formname]') ||
                          window.location.search.includes('showview=dataform') ||
                          (event.target.id && event.target.id.includes('entryform'));
        
        if (isDataform) {
          // Replace current history entry with landing page URL
          // This ensures back button goes directly to landing page (e.g., enquiry front report)
          const landingUrl = window._gridEntryLandingUrl;
          const pushStateUrl = landingUrl.startsWith('http') 
            ? new URL(landingUrl).pathname + new URL(landingUrl).search
            : landingUrl;
          
          // Store in history state for popstate handler
          history.replaceState({ 
            fromGridEntry: true, 
            landingUrl: landingUrl,
            slug: window._gridEntrySlug 
          }, '', pushStateUrl);
          
          // Mark that we've set up history for this navigation
          window._gridEntryNavigation = true;
        }
      }
    });

    // Handle modal closing and reset
    const originalCloseModal = window.closeModal;
    window.closeModal = function(name, triggerOnCloseSelector) {
      if (name === 'attachment') {
        // Reset to empty container for next use
        const modalContainer = document.getElementById('attachmentModal');
        if (modalContainer) {
          modalContainer.outerHTML = '<div id="attachmentModal" style="display: none;"></div>';
        }
      } else {
        originalCloseModal(name, triggerOnCloseSelector);
      }
      if (name === 'attachment') {
        setTimeout(() => ag.reinitializeHtmx(), 50);
      }
      if(triggerOnCloseSelector){
        window.htmx.trigger(triggerOnCloseSelector, "close");
      }
    };

    // replace fn names with functions if the fn name starts with window.
    // Keep this list in sync with any grid events we allow passing as strings from the backend.
    //
    // IMPORTANT:
    // Some pages load page-specific scripts (that define window.* callbacks) after ag-grid init.
    // If we only resolve handlers once, the callback never gets wired and "events don't fire".
    // To make this robust, we late-bind window.* references via a wrapper when the function
    // isn't available yet at init time.
    const lateBindWindowFn = (ref) => {
      if (typeof ref !== 'string') return null;
      const normalized = ref.startsWith('js:') ? ref.slice(3) : ref;
      if (!normalized.startsWith('window.')) return null;
      const name = normalized.slice('window.'.length);
      const immediate = window[name];
      if (typeof immediate === 'function') return immediate;
      let warned = false;
      return function lateBoundHandler(...args) {
        const fn = window[name];
        if (typeof fn === 'function') return fn(...args);
        if (!warned) {
          warned = true;
          console.warn(`aggrid: handler ${ref} not found on window (yet).`, { name, args });
        }
        return undefined;
      };
    };

    ['onSelectionChanged', 'onCellEditingStopped', "onGridReady", "onGridSizeChanged", "onFirstDataRendered", "onRowDataUpdated", "onFilterChanged", "onSortChanged", "onCellValueChanged", "onCellClicked", "onCellDoubleClicked", "onRowClicked", "onRowDoubleClicked", "isRowSelectable", "getRowStyle", "getRowId", "onModelUpdated", "postSortRows"].forEach((key) => {
      if (typeof gridOptions[key] === 'string') {
        // Mark this grid element if it specifically uses selectOrdersToApprove as its selection handler.
        // This lets onCellClicked distinguish approval grids from plain report grids.
        if (key === 'onSelectionChanged' && gridOptions[key] === 'window.selectOrdersToApprove') {
          el.dataset.usesApprovalHandler = 'true';
        }
        const bound = lateBindWindowFn(gridOptions[key]);
        if (bound) gridOptions[key] = bound;
      }
      if (Array.isArray(gridOptions[key])) {
        const fns = gridOptions[key].map((fnRef) => {
          if (typeof fnRef === 'function') return fnRef;
          const bound = lateBindWindowFn(fnRef);
          return bound || null;
        }).filter(Boolean);

        gridOptions[key] = (...args) => {
          fns.forEach(fn => {
            try {
              fn(...args);
            } catch (e) {
              console.error('aggrid: error in event handler', key, e);
            }
          });
        };
      }
    });
    const bindColFn = (colDef, key) => {
      if (
        typeof colDef[key] === 'string'
        && (colDef[key].startsWith('window.') || colDef[key].startsWith('js:window.'))
      ) {
        const bound = lateBindWindowFn(colDef[key]);
        if (bound) colDef[key] = bound;
      }
    };
    const bindColComponent = (colDef, key) => {
      if (
        typeof colDef[key] === 'string'
        && (colDef[key].startsWith('window.') || colDef[key].startsWith('js:window.'))
      ) {
        const name = colDef[key].replace(/^js:/, '').replace(/^window\./, '');
        if (typeof window[name] === 'function') {
          colDef[key] = window[name];
        }
      }
    };
    const numericGridValue = (value) => {
      if (value === null || value === undefined || value === '') return 0;
      if (typeof value === 'number') return Number.isNaN(value) ? 0 : value;
      const parsed = Number(String(value).replace(/[,\s]/g, '').replace(/%$/, ''));
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    const compareGridValues = (left, right) => {
      const leftText = String(left ?? '').trim();
      const rightText = String(right ?? '').trim();
      const leftNum = Number(leftText.replace(/,/g, ''));
      const rightNum = Number(rightText.replace(/,/g, ''));
      if (!Number.isNaN(leftNum) && !Number.isNaN(rightNum)) return leftNum - rightNum;
      return leftText.localeCompare(rightText, undefined, { numeric: true, sensitivity: 'base' });
    };
    const leafNodes = (node, out = []) => {
      if (!node) return out;
      if (node.data && !node.footer && !node.group && !node.rowPinned) {
        out.push(node);
        return out;
      }
      (
        node.childrenAfterFilter ||
        node.childrenAfterAggFilter ||
        node.childrenAfterSort ||
        node.childrenAfterGroup ||
        node.allLeafChildren ||
        []
      ).forEach(child => leafNodes(child, out));
      return out;
    };
    const cellValueForNode = (colDef, node, api) => {
      if (!node || !node.data) return 0;
      const getter = typeof colDef.__smaacSpanValueGetter === 'function'
        ? colDef.__smaacSpanValueGetter
        : colDef.valueGetter;
      if (typeof getter === 'function') {
        return getter({
          api,
          colDef,
          data: node.data,
          node,
          rowNode: node,
        });
      }
      return node.data[colDef.field];
    };
    const isSameSpanCell = (colDef, left, right) => {
      if (!colDef || !colDef.spanBy || !left?.data || !right?.data) return false;
      return (
        left.data[colDef.spanBy] === right.data[colDef.spanBy] &&
        left.data[colDef.field] === right.data[colDef.field]
      );
    };
    const isDisplayedSpanStart = (colDef, node, api) => {
      if (!colDef || !colDef.spanBy || !node?.data) return true;
      const rowIndex = node.rowIndex;
      if (
        Number.isInteger(rowIndex) &&
        rowIndex > 0 &&
        api &&
        typeof api.getDisplayedRowAtIndex === 'function'
      ) {
        return !isSameSpanCell(colDef, api.getDisplayedRowAtIndex(rowIndex - 1), node);
      }
      return true;
    };
    const displayedLeafTotal = (colDef, api, spanStartsOnly = false) => {
      let total = 0;
      if (!api || typeof api.getDisplayedRowCount !== 'function' || typeof api.getDisplayedRowAtIndex !== 'function') {
        return total;
      }
      const rowCount = api.getDisplayedRowCount();
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
        const node = api.getDisplayedRowAtIndex(rowIndex);
        if (node && node.data && !node.footer && !node.group && !node.rowPinned && node.level !== -1) {
          if (spanStartsOnly && !isDisplayedSpanStart(colDef, node, api)) continue;
          total += numericGridValue(cellValueForNode(colDef, node, api));
        }
      }
      return total;
    };
    const spannedTotal = (colDef) => (params) => {
      const isGrandTotal = params?.rowNode?.level === -1;
      if (isGrandTotal) {
        return displayedLeafTotal(colDef, params && params.api, true) || null;
      }
      const nodes = leafNodes(params && params.rowNode);
      if (!nodes.length) {
        return (params?.values || []).reduce((sum, value) => sum + numericGridValue(value), 0) || null;
      }
      return nodes.reduce((sum, node, index) => {
        if (index > 0 && isSameSpanCell(colDef, nodes[index - 1], node)) return sum;
        return sum + numericGridValue(cellValueForNode(colDef, node, params && params.api));
      }, 0) || null;
    };
    let hasSpanBy = Array.isArray(gridOptions.columnDefs)
      && gridOptions.columnDefs.some(colDef => colDef && colDef.spanBy);
    const spanColumnKeys = () => (
      Array.isArray(gridOptions.columnDefs)
        ? gridOptions.columnDefs
            .filter(colDef => colDef && colDef.spanBy)
            .map(colDef => colDef.colId || colDef.field)
            .filter(Boolean)
        : []
    );
    const clearSpanRowCaches = (api) => {
      const colDefs = api && typeof api.getColumnDefs === 'function' ? api.getColumnDefs() : gridOptions.columnDefs;
      if (Array.isArray(colDefs)) {
        colDefs.forEach((colDef) => {
          if (colDef && colDef.rowSpanValue) colDef.rowSpanValue = {};
        });
      }
    };
    const refreshSpanRows = (params, options = {}) => {
      if (!hasSpanBy) return;
      const api = params && params.api;
      if (!api) return;
      if (api.__smaacSpanRefreshQueued) return;
      api.__smaacSpanRefreshQueued = true;
      window.requestAnimationFrame(() => {
        try {
          api.__smaacSpanRefreshing = true;
          clearSpanRowCaches(api);
          if (typeof api.refreshClientSideRowModel === 'function') {
            api.refreshClientSideRowModel('aggregate');
          }
          if (typeof api.refreshCells === 'function') {
            const refreshParams = { force: true };
            if (options.spanColumnsOnly) refreshParams.columns = spanColumnKeys();
            api.refreshCells(refreshParams);
          }
          if (!options.skipRedrawRows && typeof api.redrawRows === 'function') api.redrawRows();
          if (typeof api.resetRowHeights === 'function') api.resetRowHeights();
        } finally {
          api.__smaacSpanRefreshing = false;
          api.__smaacSpanRefreshQueued = false;
        }
      });
    };
    const refreshSpanRowsAfterFilterSettles = (params) => {
      if (!hasSpanBy) return;
      const api = params && params.api;
      if (!api) return;
      if (api.__smaacSpanRefreshing) return;
      clearSpanRowCaches(api);
      window.clearTimeout(api.__smaacSpanFilterRefreshTimer);
      api.__smaacSpanFilterRefreshTimer = window.setTimeout(() => {
        refreshSpanRows(params, { spanColumnsOnly: true, skipRedrawRows: true });
      }, 450);
    };
    const attachGroupedPostSortRows = () => {
      const groupField = gridOptions.keepRowsTogetherBy;
      if (!groupField) return;
      const sortField = gridOptions.sortWithinGroupBy;
      const existingPostSortRows = gridOptions.postSortRows;

      gridOptions.rowClassRules = {
        ...(gridOptions.rowClassRules || {}),
        'smaac-group-start-row': params => Boolean(params.data && params.data.__smaac_group_start),
      };
      gridOptions.postSortRows = (params) => {
        if (typeof existingPostSortRows === 'function') existingPostSortRows(params);
        if (!params || !Array.isArray(params.nodes) || params.nodes.length < 2) return;

        const groups = new Map();
        const order = [];
        params.nodes.forEach((node, idx) => {
          const value = node?.data ? node.data[groupField] : undefined;
          const key = value === undefined || value === null || value === '' ? `__node_${idx}` : String(value);
          if (!groups.has(key)) {
            groups.set(key, []);
            order.push(key);
          }
          groups.get(key).push(node);
        });

        const sorted = [];
        order.forEach((key, groupIndex) => {
          const nodes = groups.get(key) || [];
          if (sortField) {
            nodes.sort((left, right) => compareGridValues(left?.data?.[sortField], right?.data?.[sortField]));
          }
          nodes.forEach((node, nodeIndex) => {
            if (node?.data) node.data.__smaac_group_start = groupIndex > 0 && nodeIndex === 0;
            sorted.push(node);
          });
        });
        params.nodes.splice(0, params.nodes.length, ...sorted);
      };
    };
    const mergeBaseHandler = (key, baseHandler) => {
      const existing = gridOptions[key];
      if (existing === baseHandler) return;
      if (typeof existing === 'function') {
        gridOptions[key] = (...args) => {
          baseHandler(...args);
          existing(...args);
        };
        return;
      }
      gridOptions[key] = baseHandler;
    };

    attachGroupedPostSortRows();
    mergeBaseHandler('onGridReady', baseOnGridReady);
    mergeBaseHandler('onGridSizeChanged', baseOnGridSizeChanged);
    mergeBaseHandler('onFirstDataRendered', baseOnFirstDataRendered);
    mergeBaseHandler('onRowDataUpdated', baseOnRowDataUpdated);
    if (hasSpanBy) {
      const spanModelHandler = gridOptions.deferSpanRefreshOnFilter
        ? refreshSpanRowsAfterFilterSettles
        : refreshSpanRows;
      mergeBaseHandler('onFilterChanged', spanModelHandler);
      mergeBaseHandler('onSortChanged', refreshSpanRows);
      mergeBaseHandler('onModelUpdated', spanModelHandler);
    }

    const usesApprovalSelection = gridOptions.approvalSelection === true || el.dataset.usesApprovalHandler === 'true';
    const customIsRowSelectable = gridOptions.isRowSelectable;
    gridOptions.isRowSelectable = function (rowNode) {
      if (!defaultIsRowSelectable(rowNode)) return false;
      if (
        typeof customIsRowSelectable === 'function'
        && customIsRowSelectable !== defaultIsRowSelectable
        && !customIsRowSelectable(rowNode)
      ) {
        return false;
      }
      if (usesApprovalSelection && !isApprovalSelectableRow(rowNode && rowNode.data)) {
        return false;
      }
      return true;
    };
    if (
      gridOptions.rowSelection
      && typeof gridOptions.rowSelection === 'object'
      && gridOptions.rowSelection.hideDisabledCheckboxes === true
      && typeof gridOptions.rowSelection.isRowSelectable !== 'function'
    ) {
      gridOptions.rowSelection.isRowSelectable = gridOptions.isRowSelectable;
    }

    // Approval listing grids (e.g. indentssr): onSelectionChanged only ran selectOrdersToApprove;
    // checkbox selection never reached onCellClicked, so comment/PDF stayed disabled.
    if (
      el.dataset.usesApprovalHandler === 'true'
      && (gridOptions.grid_type === 'report' || gridOptions.grid_type === 'groupreport')
    ) {
      const prevOnSelectionChanged = gridOptions.onSelectionChanged;
      gridOptions.onSelectionChanged = function (event) {
        if (el._suppressSelectionChanged) return;
        if (typeof prevOnSelectionChanged === 'function') {
          try {
            prevOnSelectionChanged(event);
          } catch (e) {
            console.error('aggrid: onSelectionChanged', e);
          }
        }
        try {
          const rows = event.api.getSelectedRows() || [];
          const row = rows[0];
          if (row && row.id !== undefined && row.id !== null && row.id !== '') {
            wireReportGridCommentAndPrint(el, row.id, row);
          } else {
            wireReportGridCommentAndPrint(el, null);
          }
        } catch (_e) {}
      };
    }

    const addHeaderClass = (colDef, className) => {
      if (!className) return;
      const existing = colDef.headerClass;
      if (!existing) {
        colDef.headerClass = className;
        return;
      }
      if (typeof existing === 'string') {
        const parts = existing.split(/\s+/).filter(Boolean);
        if (!parts.includes(className)) colDef.headerClass = `${existing} ${className}`.trim();
        return;
      }
      if (Array.isArray(existing)) {
        if (!existing.includes(className)) colDef.headerClass = [...existing, className];
        return;
      }
      if (typeof existing === 'function') {
        colDef.headerClass = (params) => {
          const res = existing(params);
          if (!res) return className;
          if (typeof res === 'string') {
            const parts = res.split(/\s+/).filter(Boolean);
            return parts.includes(className) ? res : `${res} ${className}`.trim();
          }
          if (Array.isArray(res)) {
            return res.includes(className) ? res : [...res, className];
          }
          return className;
        };
        return;
      }
      // Unknown type - fall back to overriding
      colDef.headerClass = className;
    };
    const compileStringCellClassRules = (colDef) => {
      const rules = colDef && colDef.cellClassRules;
      if (!rules || typeof rules !== 'object') return;
      Object.keys(rules).forEach((className) => {
        const rule = rules[className];
        if (typeof rule === 'function') return;
        if (typeof rule !== 'string') return;
        const trimmed = rule.trim();
        try {
          rules[className] = new Function(
            'x',
            `
              const params = x || {};
              const { value, data, node, api, context, colDef, column, rowIndex } = params;
              return Boolean(${trimmed});
            `,
          );
        } catch (_e) {
          console.warn('aggrid: cellClassRules compile failed', className, trimmed);
          rules[className] = () => false;
        }
      });
    };
    const addCellClassRule = (colDef, className, rule) => {
      if (!colDef) return;
      if (!colDef.cellClassRules || typeof colDef.cellClassRules !== 'object') {
        colDef.cellClassRules = {};
      }
      if (!colDef.cellClassRules[className]) {
        colDef.cellClassRules[className] = rule;
      }
    };
    gridOptions.columnDefs.forEach(colDef => {
      compileStringCellClassRules(colDef);
      // Add required indicator in header when backend marks the column mandatory.
      if (colDef && (colDef.required === true || colDef.mandatory === true || colDef.isMandatory === true || colDef.IsMandatory === true)) {
        addHeaderClass(colDef, 'ag-required-header');
      }
      const headerInfo = colDef.headerInfo || '';
      if (headerInfo) {
        colDef.headerInfo = headerInfo;
        colDef.headerComponentParams = {
          ...(colDef.headerComponentParams || {}),
          innerHeaderComponent: colDef.headerComponentParams?.innerHeaderComponent || 'headerInfoComponent',
          innerHeaderComponentParams: {
            ...(colDef.headerComponentParams?.innerHeaderComponentParams || {}),
            ...(colDef.innerHeaderComponentParams || {}),
            headerInfo,
          },
        };
        delete colDef.innerHeaderComponent;
        delete colDef.innerHeaderComponentParams;
      } else if (colDef.innerHeaderComponent || colDef.innerHeaderComponentParams) {
        colDef.headerComponentParams = {
          ...(colDef.headerComponentParams || {}),
          innerHeaderComponent: colDef.innerHeaderComponent,
          innerHeaderComponentParams: {
            ...(colDef.headerComponentParams?.innerHeaderComponentParams || {}),
            ...(colDef.innerHeaderComponentParams || {}),
          },
        };
        delete colDef.innerHeaderComponent;
        delete colDef.innerHeaderComponentParams;
      }
      addCellClassRule(colDef, 'editable-cell', isAgGridEditableCell);
      addCellClassRule(colDef, 'editable-required-cell', isAgGridRequiredCell);
      addCellClassRule(
        colDef,
        'editable-optional-cell',
        params => isAgGridEditableCell(params) && !isAgGridRequiredCell(params),
      );
      if(typeof colDef.editable === 'string' && colDef.editable.startsWith('window.')){
        colDef.editable = lateBindWindowFn(colDef.editable);
      }
      if (typeof colDef.editable === 'string' && !colDef.editable.startsWith('window.')) {
        var raw = colDef.editable.trim();
        if (raw === 'true') { colDef.editable = true; }
        else if (raw === 'false') { colDef.editable = false; }
        else if (/^window\.[A-Za-z_$][A-Za-z0-9_$]*$/.test(raw)) {
          colDef.editable = lateBindWindowFn(raw);
        }
        else {
          try { colDef.editable = new Function('params', 'return Boolean(' + raw + ');'); }
          catch (_e) { colDef.editable = false; }
        }
      }
      
      // Ensure editable columns have proper cell editors for keyboard navigation
      if (colDef.editable === true || (typeof colDef.editable === 'function')) {
        if (!colDef.cellEditor) {
          // Set default cell editor based on data type
          if (colDef.cellEditor === undefined) {
            colDef.cellEditor = 'agTextCellEditor'; // Default to text editor
          }
        }
        // Ensure proper navigation settings
        colDef.suppressNavigable = false;
      }
      if(colDef.headerCheckboxSelection){
        colDef.checkboxSelection = function(params) {
          return params.node.level !== -1;
        }
      }
      if (colDef.filter === 'agDateColumnFilter') {
        colDef.filterParams = {
          ...(colDef.filterParams || {}),
          comparator,
        };
      }
      addClearFilterOption(colDef);
      if (colDef.valueGetter ){
        colDef.valueGetter = window[colDef.valueGetter];
      }
      bindColFn(colDef, 'valueFormatter');
      bindColFn(colDef, 'valueParser');
      bindColFn(colDef, 'valueSetter');
      bindColFn(colDef, 'cellEditorParams');
      bindColComponent(colDef, 'cellEditor');
      const fieldKey = String(colDef.field || '').toLowerCase();
      const suppressTotals =
        colDef.noTotals === true ||
        colDef.noTotals === 'true' ||
        fieldKey === 'awsfile_count' ||
        colDef.cellRenderer === 'agAwsAttachmentCell';
      if (suppressTotals) {
        colDef.noTotals = true;
        const existingVF = colDef.valueFormatter;
        colDef.aggFunc = () => null;
        colDef.valueFormatter = (params) => {
          const isFooter = params?.node && (params.node.footer || params.node.level === -1 || params.node.rowPinned === 'bottom');
          if (isFooter) return '';
          return typeof existingVF === 'function' ? existingVF(params) : params.value;
        };
      }
      // Allow aggFunc to be passed as "window.someFn" from the backend.
      // Use late binding so page-specific scripts can define the function after grid init.
      if (
        typeof colDef.aggFunc === 'string'
        && (colDef.aggFunc.startsWith('window.') || colDef.aggFunc.startsWith('js:window.'))
      ) {
        const bound = lateBindWindowFn(colDef.aggFunc);
        if (bound) colDef.aggFunc = bound;
      }
      if (
        typeof colDef.cellStyle === 'string'
        && (colDef.cellStyle.startsWith('window.') || colDef.cellStyle.startsWith('js:window.'))
      ) {
        const bound = lateBindWindowFn(colDef.cellStyle);
        if (bound) colDef.cellStyle = bound;
      }
      if (colDef.spanBy) {
        hasSpanBy = true;
        if (!suppressTotals && !colDef.aggFunc) {
          colDef.aggFunc = spannedTotal(colDef);
        }
        colDef.rowSpan = function(params){
          if (!params.data) return 1;
          
          const currentValue = params.data[colDef.spanBy];
          const currentColValue = params.data[colDef.field];
          const rowIndex = params.node.rowIndex;
          let span = 1;
          // First row should determine the span
          if(!params.colDef.rowSpanValue) params.colDef.rowSpanValue = {}
          if (rowIndex > 0) {
              const previousRow = params.api.getDisplayedRowAtIndex(rowIndex - 1);
              if (previousRow && previousRow.data
                && previousRow.data[colDef.spanBy] === currentValue
                && previousRow.data[colDef.field] === currentColValue
              ) {
              params.colDef.rowSpanValue[params.node.rowIndex] = 0;
              return 0; // This cell should be hidden
              }
          }
          // Count following rows with same value
          while (
              rowIndex + span < params.api.getDisplayedRowCount()
          ) {
              const nextRow = params.api.getDisplayedRowAtIndex(rowIndex + span);
              if (
                !nextRow ||
                !nextRow.data ||
                nextRow.data[colDef.spanBy] !== currentValue ||
                nextRow.data[colDef.field] !== currentColValue
              ) break;
              span++;
          }        
          params.colDef.rowSpanValue[params.node.rowIndex] = span;
          return span;
        };
        
        // Add these properties for better row spanning.
        // A total/footer row (group footer, any pinned row, group row, or level -1 row) is NOT a
        // spanned-over data cell: skip it, otherwise blank-cell (display:none) hides the column
        // total. rowSpanValue is a rowIndex-keyed cache that goes stale across filter/data changes,
        // so the footer cell could resolve to 0 and vanish — exactly why spanned totals
        // disappeared after filtering while non-spanned totals stayed.
        const isTotalRow = params =>
          !!(params.node && (params.node.footer || params.node.rowPinned || params.node.group || params.node.level === -1));
        // node.rowIndex is the canonical key: rowSpanValue is written with params.node.rowIndex in
        // rowSpan(), so read it the same way here to keep a single source of truth.
        colDef.cellClassRules = {
          ...(colDef.cellClassRules || {}),
          'span-cell': params => {
            return !isTotalRow(params) && params.colDef.rowSpanValue[params.node.rowIndex] > 0
          },
          'blank-cell': params => {
            return !isTotalRow(params) && params.colDef.rowSpanValue[params.node.rowIndex] === 0
          }
        };
        // This valueGetter function determines the value of a cell in AG Grid.
        // If the cell is covered by the row span of another cell (rowSpan returns 0),
        // it sets the value to 0 to ensure that the total value is calculated correctly.
        const defaultValueGettr = (params) => {
          return params.data ? params.data[params.colDef.field] : params.value
        }
        const oldGetter = colDef.valueGetter || defaultValueGettr;
        colDef.__smaacSpanValueGetter = oldGetter;
        colDef.valueGetter = (params) => {
          return params.colDef.rowSpan(params) == 0 ? 0 : oldGetter(params)
        }
        if (!colDef.filterValueGetter) {
          colDef.filterValueGetter = oldGetter;
        }
      }
      
      // Right align numeric and percentage-like values (skip for jobid)
      // Skip right alignment for id/name text columns
      const headerKey = String(colDef.headerName || '').toLowerCase().replace(/\s+/g, '');
      const skipKeys = new Set(['jobid', 'jobname', 'client', 'partyname', 'orderno']);
      const skipRightAlign = skipKeys.has(fieldKey) || skipKeys.has(headerKey);
      const isNumericLike = (val) => {
        if (val === null || val === undefined || val === '') return false;
        if (typeof val === 'number') return true;
        if (typeof val !== 'string') return false;
        const cleaned = val.replace(/[\,\s]/g, '').replace(/%$/, '').replace(/^[-+]?\((.*)\)$/,'$1');
        return cleaned !== '' && !isNaN(Number(cleaned));
      };
      // identify date-like columns
      const isDateCol = (colDef.filter === 'agDateColumnFilter') || fieldKey.includes('date') || headerKey.includes('date');
      // detect if this column holds numeric data (for totals + data-row formatting)
      // IMPORTANT: backend often formats numbers as strings with commas (e.g. "1,234").
      const hasNumericData = Array.isArray(rowData) && rowData.some(r => r && isNumericLike(r[colDef.field]));
      // If any non-empty value in the column is textual, treat the whole column as text.
      // This keeps mixed columns like Phase No ("1", "5A", "Block-C&D") consistently left aligned.
      const hasTextData = !isDateCol && Array.isArray(rowData) && rowData.some(r => {
        const value = r && r[colDef.field];
        if (value === null || value === undefined) return false;
        if (typeof value !== 'string') return false;
        const trimmed = value.trim();
        return trimmed !== '' && !isNumericLike(trimmed);
      });
      // identify explicit text-typed columns (backend can set type: 'text')
      const isTextType = hasTextData
        || (typeof colDef.type === 'string' && colDef.type.toLowerCase() === 'text')
        || (Array.isArray(colDef.type) && colDef.type.map(t => String(t).toLowerCase()).includes('text'));
      if (!skipRightAlign && !isTextType) {
        // Mark as numeric so Excel export writes real numbers, not formatted text.
        colDef.__exportAsNumber = hasNumericData;
        colDef.cellClassRules = {
          ...(colDef.cellClassRules || {}),
          'ag-right-aligned-cell': (params) => isNumericLike(params.value)
        };
        // Ensure some right padding for numeric cells (to avoid sticking to separator)
        const originalCellStyle = colDef.cellStyle;
        colDef.cellStyle = (params) => {
          let base = {};
          if (typeof originalCellStyle === 'function') {
            try { base = originalCellStyle(params) || {}; } catch (_e) { base = {}; }
          } else if (originalCellStyle && typeof originalCellStyle === 'object') {
            base = { ...originalCellStyle };
          }
          if (isNumericLike(params.value)) {
            base['padding-right'] = '8px';
          }
          return base;
        };

        // Format numeric cells based on country configuration.
        // Columns can opt into fixed decimals or blank-zero display.
        const existingVF = colDef.valueFormatter;
        colDef.valueFormatter = (params) => {
          const isFooter = params?.node && (params.node.level === -1 || params.node.rowPinned === 'bottom');
          if (suppressTotals && isFooter) return '';
          if (isNumericLike(params.value) && !isDateCol && !isTextType) {
            const raw = typeof params.value === 'number' ? params.value : Number(String(params.value).replace(/,/g, ''));
            if (!isNaN(raw)) {
              if (colDef.blankZero && raw === 0 && !isFooter) {
                return '';
              }
              const minFractionDigits =
                typeof colDef.minFractionDigits === 'number' ? colDef.minFractionDigits : 0;
              const maxFractionDigits =
                typeof colDef.maxFractionDigits === 'number' ? colDef.maxFractionDigits : 2;
              try {
                const country = el.dataset.country || 'india';
                const locale = country.toLowerCase() === 'india' ? 'en-IN' : 'en-US';
                return new Intl.NumberFormat(locale, {
                  minimumFractionDigits: minFractionDigits,
                  maximumFractionDigits: maxFractionDigits,
                }).format(raw);
              } catch (_e) {
                const country = el.dataset.country || 'india';
                const locale = country.toLowerCase() === 'india' ? 'en-IN' : 'en-US';
                return raw.toLocaleString(locale, {
                  minimumFractionDigits: minFractionDigits,
                  maximumFractionDigits: maxFractionDigits,
                });
              }
            }
          }
          return typeof existingVF === 'function' ? existingVF(params) : params.value;
        };
      }
      // Also suppress totals for these id/name columns
      // When rowData is empty, don't suppress — data may load dynamically later.
      const hasRows = Array.isArray(rowData) && rowData.length > 0;
      if (!suppressTotals && (skipRightAlign || isDateCol || (hasRows && !hasNumericData) || isTextType)) {
        colDef.aggFunc = () => null; // no total
        const existingVF = colDef.valueFormatter;
        colDef.valueFormatter = (params) => {
          const isFooter = params?.node && (params.node.level === -1 || params.node.rowPinned === 'bottom');
          if (isFooter) {
            // Initial rowData may omit computed numeric columns (e.g. amount/gst on dataforms).
            // Pinned-bottom / grand-total rows still carry real totals; format them instead of hiding.
            if (isNumericLike(params.value)) {
              const raw =
                typeof params.value === 'number'
                  ? params.value
                  : Number(String(params.value).replace(/,/g, ''));
              if (!isNaN(raw)) {
                const minFractionDigits =
                  typeof colDef.minFractionDigits === 'number' ? colDef.minFractionDigits : 0;
                const maxFractionDigits =
                  typeof colDef.maxFractionDigits === 'number' ? colDef.maxFractionDigits : 2;
                try {
                  const country = el.dataset.country || 'india';
                  const locale = country.toLowerCase() === 'india' ? 'en-IN' : 'en-US';
                  return new Intl.NumberFormat(locale, {
                    minimumFractionDigits: minFractionDigits,
                    maximumFractionDigits: maxFractionDigits,
                  }).format(raw);
                } catch (_e) {
                  return String(raw);
                }
              }
            }
            return typeof existingVF === 'function' ? existingVF(params) : params.value;
          }
          return typeof existingVF === 'function' ? existingVF(params) : params.value;
        };
      }

      
      if (colDef.field) {
        const rowCellClassKey = `${colDef.field}.cellClass`;
        const originalCellClass = colDef.cellClass;
        colDef.cellClass = function(params) {
          const classes = [];
          const addClasses = (value) => {
            if (!value) return;
            if (typeof value === 'string') {
              classes.push(value);
            } else if (Array.isArray(value)) {
              classes.push(...value.filter(Boolean));
            }
          };
          if (typeof originalCellClass === 'function') {
            addClasses(originalCellClass(params));
          } else {
            addClasses(originalCellClass);
          }
          if (params.data) {
            addClasses(params.data[rowCellClassKey]);
          }
          return classes.join(' ');
        };
      }
      const isSharedNumericColumn = isQtyAmountOrWeightColumn(colDef);
      if (isSharedNumericColumn && colDef.cellEditor === 'agTextCellEditor') {
        colDef.cellEditor = 'agNumberCellEditor';
      }
      if (
        !colDef.valueParser
        && (isSharedNumericColumn || colDef.cellEditor === 'agNumberCellEditor')
      ) {
        colDef.valueParser = params =>
          parseAgGridNumber(params.newValue, params.oldValue, colDef.roundPrecision);
      }
        
      if (colDef.calTotalFor) {
        colDef.valueFormatter = params => {
            if (params.node.level === -1) {
                let total = 0;
                let qtyTotal = 0;
                params.api.forEachNodeAfterFilterAndSort(node => {
                    if (node.level !== -1 && node.data && node.rowPinned !== 'bottom') {
                        const sourceFieldValue = node.data[colDef.field];
                        if (sourceFieldValue) {
                            const targetFieldValue = parseFloat(node.data[colDef.calTotalFor]) || 0;
                            total += targetFieldValue;
                            if (colDef.calQtyFor) {
                              const q = parseFloat(node.data[colDef.calQtyFor]) || 0;
                              qtyTotal += q;
                            }
                        }
                    }
                });
                if (colDef.calQtyFor) {
                  return `${Math.round(total)} (${Math.round(qtyTotal)})`;
                }
                return `${Math.round(total)}`;
            }
            return params.value;
        };
      }
    })
    function comparator  (filterLocalDateAtMidnight, cellValue) {
      if (cellValue === null || cellValue === undefined || cellValue === '') {
        return -1;
      }
      let cellDate = null;
      const atMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (cellValue instanceof Date) {
        cellDate = atMidnight(cellValue);
      } else if (typeof cellValue === 'number') {
        const d = new Date(cellValue);
        if (!isNaN(d)) cellDate = atMidnight(d);
      } else if (typeof cellValue === 'string') {
        const s = cellValue.trim();
        const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
        if (m) {
          const day = parseInt(m[1], 10);
          const month = parseInt(m[2], 10);
          let year = parseInt(m[3], 10);
          if (year < 100) year += 2000; // handle yy
          cellDate = new Date(year, month - 1, day);
        } else {
          const d = new Date(s);
          if (!isNaN(d)) cellDate = atMidnight(d);
        }
      }

      if (!cellDate || isNaN(cellDate)) {
        return -1;
      }
      const filterDate = atMidnight(filterLocalDateAtMidnight);
      if (cellDate.getTime() === filterDate.getTime()) return 0;
      return cellDate < filterDate ? -1 : 1;
    }
    function runDynamicColumnSizing(gridApi, columnDefs) {
      const columnCount = columnDefs.length;
      const agRoot = el?.querySelector('.ag-root') || el?.querySelector('.ag-root-wrapper') || el;
      const gridWidth = agRoot ? agRoot.clientWidth : 0;
      
      let COLUMN_THRESHOLD = 15;
      const MIN_COLUMN_WIDTH = 70;
      if (!gridWidth || columnCount === 0) return false;
      if (Array.isArray(columnDefs) && columnDefs.some(col => col.hasOwnProperty('tv'))) {
          COLUMN_THRESHOLD = 12;
      }
      const totalMinWidth = columnCount * MIN_COLUMN_WIDTH;
      if (columnCount <= COLUMN_THRESHOLD && gridWidth >= totalMinWidth) {
          gridApi.sizeColumnsToFit({
              defaultMinWidth: MIN_COLUMN_WIDTH
          });
      } else {
          gridApi.autoSizeAllColumns(gridOptions.skipHeaderOnAutoSize === true);
          
          const displayedColumns = typeof gridApi.getAllDisplayedColumns === 'function'
            ? (gridApi.getAllDisplayedColumns() || [])
            : [];
          const totalWidth = displayedColumns.reduce((width, column) => {
              return width + (column?.getActualWidth?.() || 0);
          }, 0);
          
          if (totalWidth < gridWidth) {
              gridApi.sizeColumnsToFit({
                  defaultMinWidth: MIN_COLUMN_WIDTH
              });
          }
      }
      return true;
    }

    function animateDynamicColumnSizing(gridApi, columnDefs) {
      const fromWidths = getDisplayedColumnWidths(gridApi);
      if (fromWidths.length === 0) {
        runDynamicColumnSizing(gridApi, columnDefs);
        return;
      }

      runDynamicColumnSizing(gridApi, columnDefs);
      const toWidths = getDisplayedColumnWidths(gridApi);

      setDisplayedColumnWidths(gridApi, fromWidths, false);
      animateColumnSizing(gridApi, fromWidths, toWidths);
    }
    function completeInitialColumnSizing(gridApi, columnDefs) {
      if (!gridApi || gridApi.isDestroyed?.()) return;
      const sized = runDynamicColumnSizing(gridApi, columnDefs);
      if (!sized) return;
      try {
        gridApi.flushAllAnimationFrames?.();
      } catch (_e) {}
      hasCompletedInitialColumnSizing = true;
      relaxColumnMaxWidthAfterInitialSizing(gridApi);
      runOnNextFrame(() => {
        setInitialColumnSizingPendingState(false);
      });
    }
    setInitialColumnSizingPendingState(true);
    gridOptions.el = el;
    const gridApi = createGrid(el, gridOptions);
    window[`aggridApi:${el.dataset.agId}`] = gridApi;
    bindManualRowResize(gridApi, el, gridOptions);
    bindSelectionCheckboxWrapperClick(gridApi);
    bindHeaderSelectAllCheckboxClick(gridApi);

    const persistGridState = () => {
      if (!gridOptions.disablePersistentGridState) {
        savePersistentGridState(gridApi, el.dataset.agId);
      }
    };
    [
      'filterChanged',
      'sortChanged',
      'columnMoved',
      'columnPinned',
      'columnVisible',
      'columnResized',
      'columnRowGroupChanged',
      'columnValueChanged',
      'columnPivotChanged',
      'displayedColumnsChanged',
      'bodyScrollEnd',
    ].forEach((eventName) => {
      try {
        gridApi.addEventListener(eventName, persistGridState);
      } catch (_e) {}
    });

    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(function() {
        if (!gridApi || gridApi.isDestroyed?.()) {
          resizeObserver.disconnect();
          return;
        }
        scheduleDynamicColumnSizing(gridApi, gridOptions.columnDefs);
      });
      resizeObserver.observe(el);
      gridApi.addEventListener('gridPreDestroyed', function() {
        savePersistentGridStateNow(gridApi, el.dataset.agId);
        resizeObserver.disconnect();
      });
    } else {
      gridApi.addEventListener('gridPreDestroyed', function() {
        savePersistentGridStateNow(gridApi, el.dataset.agId);
      });
    }

    // Inject print-friendly toggle buttons
    try { ag._injectPrintButtons(el); } catch (_e) {}

    // Setup browser back button handler for grid entry navigation
    // This ensures that when back is pressed after opening an entry from the grid,
    // it redirects to slug/landing with API token (e.g., enquiry front report)
    // We use capture phase to run before the global handler in common.js
    if (!window._gridPopstateHandlerAdded) {
      window._gridPopstateHandlerAdded = true;
      window.addEventListener('popstate', function(event) {
        // Check if we're navigating back from a grid entry
        const isFromGridEntry = (event.state && event.state.fromGridEntry) || window._gridEntryNavigation;
        const landingUrl = (event.state && event.state.landingUrl) || window._gridEntryLandingUrl;
        
        if (isFromGridEntry && landingUrl) {
          // Set flag to prevent global handler from reloading
          window._gridNavigationInProgress = true;
          
          // Clear the navigation flag
          window._gridEntryNavigation = false;
          window._gridEntryLandingUrl = null;
          
          // Find the grid container - try multiple selectors
          let gridElement = document.querySelector(`[data-ag-id="${el.dataset.agId}"]`);
          if (!gridElement) {
            // Try finding by slug
            const slug = (event.state && event.state.slug) || window._gridEntrySlug;
            if (slug) {
              const formContainer = document.querySelector(`[data-formname="${slug}"]`);
              if (formContainer) {
                // Find the entryform container that holds the grid
                const entryFormContainer = formContainer.closest('[id*="entryform"]') || 
                                         document.querySelector(`#${slug}_entryform`);
                if (entryFormContainer) {
                  gridElement = entryFormContainer.querySelector('[data-ag-id]');
                }
              }
            }
          }
          
          // If still not found, try to find any grid on the page
          if (!gridElement) {
            gridElement = document.querySelector('[data-ag-id]');
          }
          
          if (gridElement && htmx) {
            // Get API token
            let apitoken = null;
            const urlParams = new URLSearchParams(window.location.search);
            apitoken = urlParams.get('apitoken');
            if (!apitoken) {
              const tokenInput = document.querySelector('[name="apitoken"]');
              if (tokenInput) {
                apitoken = tokenInput.value;
              }
            }
            
            // Build landing URL with API token
            let landingUrlWithToken = landingUrl;
            if (apitoken) {
              if (landingUrl.startsWith('http://') || landingUrl.startsWith('https://')) {
                const urlObj = new URL(landingUrl);
                urlObj.searchParams.set('apitoken', apitoken);
                landingUrlWithToken = urlObj.toString();
              } else {
                const separator = landingUrl.includes('?') ? '&' : '?';
                landingUrlWithToken = `${landingUrl}${separator}apitoken=${apitoken}`;
              }
            }
            
            // Get the target ID - should be the entryform container
            const slug = (event.state && event.state.slug) || window._gridEntrySlug;
            let targetId = null;
            if (slug) {
              targetId = `${slug}_entryform`;
            } else {
              targetId = gridElement.closest('[id*="entryform"]')?.id || 
                        gridElement.dataset.agId;
            }
            
            // Update URL immediately to prevent global handler from reloading
            const pushStateUrl = landingUrlWithToken.startsWith('http') 
              ? new URL(landingUrlWithToken).pathname + new URL(landingUrlWithToken).search
              : landingUrlWithToken;
            history.replaceState({}, '', pushStateUrl);
            
            // Use htmx to navigate to landing page
            htmx.ajax('GET', landingUrlWithToken, {
              target: targetId ? `#${targetId}` : gridElement,
              swap: 'innerHTML',
              headers: { 'HX-Request': 'true' }
            });
            
            // Clear flag after a short delay to allow navigation to complete
            setTimeout(() => {
              window._gridNavigationInProgress = false;
            }, 100);
          } else {
            // If we can't find the grid, clear the flag
            window._gridNavigationInProgress = false;
          }
        }
      }, true); // Use capture phase to run before other handlers
    }

    htmx.process(el);
  },
  cellRenderer: {
    awsModalEvent: function (src) {
      document.getElementById('awsUploadFrame').src = src;
      window.openModal('awsupload');
    },
    imageCell: function(params){
      // If this becomes more than 1 line and 1 var, please refactor
      return `<img src="${params.value}" style="width: 50px; height: auto;" />`
    },
    relatedTooltipCell: function (params) {
      if (params.data === undefined) {
        return params.value;
      }
      let tip = params.data[`${params.colDef.field}.tooltip`];
      if (tip === undefined) {
        return params.value;
      }
      let html = `<span title='${tip}' style='background: pink;'>${params.value}</span>`;
      return html;
    },
    selectCell: function(params){
      return `
        <span style="display: flex; align-items: center; 
          justify-content: space-between; width: 100%;">
          <span>${params.value || ''}</span> 
          <span style="font-size: 7px; margin-left: auto;">▼</span>
        </span>
      `;
    },
    linkCell: function (params) {
      if (params.data === undefined) {
        return params.value;
      }
      let href = params.data[`${params.colDef.field}.href`];
      let cls = "";
      if(params.colDef.linkColumnName){
        href = params.data[params.colDef.linkColumnName]
      }
      if (href === undefined) {
        return params.value;
      }
      if(params.colDef.add__class){
        cls = params.colDef.add__class;
      }
      let html = `<a href="${href}" class="${cls}" target="_blank">${params.value}</a>`;
      return html;
    },
    awsAttachmentCell: function (params) {
      if (params?.node && (params.node.footer || params.node.level === -1 || params.node.rowPinned === 'bottom')) {
        return '';
      }
      let row_id = 0;
      let attachment_url = params.colDef.attachment_url || "";
      let master_table = "";
      let slug = "";
      let extra_params = '""';
      let colName = params.colDef.field;
      let triggerOnClose = "fake-selector"
      const attachment_count = params.value === undefined || params.value === null ? 0 : params.value;
      // Determine if uploads/deletes should be disabled (only view)
      // Priority: column-level setting overrides grid-level
      const gridOnlyView = (() => {
        try {
          // Prefer direct access to grid options as custom keys may not be exposed via getGridOption
          const go =
            (params?.api?.gridOptionsWrapper && params.api.gridOptionsWrapper.gridOptions) ||
            (params?.api?.getGridOption ? params.api.getGridOption('gridOptions') : null);
          const v1 = !!(go && (go.onlyview === true));
          const v2 = !!(params?.api?.getGridOption && (params.api.getGridOption('onlyview')));
          return v1 || v2;
        } catch (_e) {}
        return false;
      })();
      const colOnlyView = !!(params?.colDef?.onlyview || params?.colDef?.noUploadDelete);
      const onlyView = colOnlyView || gridOnlyView;
      if (params.data === undefined) {
        return attachment_count;
      }
      if (params.data) {
        if (params.data[`${colName}.row_id`]) {
          row_id = params.data[`${colName}.row_id`];
        }
        if(params.data[`${colName}.master_table`]){
          master_table = params.data[`${colName}.master_table`]
        }
        if (params.data[`${colName}.slug`]) {
          slug = params.data[`${colName}.slug`];
        }
        if(params.data[`${colName}.extra_params`]){
          extra_params = JSON.stringify(params.data[`${colName}.extra_params`]);
        }
        if (params.data[`${colName}.attachment_url`]) {
          attachment_url = params.data[`${colName}.attachment_url`];
        }
        if(params.data[`${colName}.triggerOnClose`]){
          triggerOnClose = params.data[`${colName}.triggerOnClose`]
        }
      }
      let html = `<button type="button" class="flex items-center justify-center sm:w-auto block rounded attachment-btn"
                    data-row-id="${row_id}"
                    data-slug="${slug}"
                    hx-vals='{"awsfile_master_pk": "${row_id}", "formname": "${slug}", "master_table": "${master_table}", "extra_params": ${extra_params}, "triggerOnClose": "${triggerOnClose}", "onlyview": ${onlyView ? true : false} }'
                    hx-post="${attachment_url}"
                    hx-trigger="click"
                    hx-swap="outerHTML"
                    hx-target="#attachmentModal"
                    hx-include="[name='apitoken']"
                    hx-indicator="#indicator"
                    hx-on::after-swap="window.ag.reinitializeHtmx();"
            >
            <span id="attachment-count-${row_id}" class="hover:bg-indigo-200 badge badge-pill badge-white text-xs">
                    ${attachment_count}<div class="ml-1">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="size-3.5 text-blue-500">
                        <path fill-rule="evenodd" d="M15.621 4.379a3 3 0 0 0-4.242 0l-7 7a3 3 0 0 0 4.241 4.243h.001l.497-.5a.75.75 0 0 1 1.064 1.057l-.498.501-.002.002a4.5 4.5 0 0 1-6.364-6.364l7-7a4.5 4.5 0 0 1 6.368 6.36l-3.455 3.553A2.625 2.625 0 1 1 9.52 9.52l3.45-3.451a.75.75 0 1 1 1.061 1.06l-3.45 3.451a1.125 1.125 0 0 0 1.587 1.595l3.454-3.553a3 3 0 0 0 0-4.242Z" clip-rule="evenodd" />
                      </svg>
                    </div>
                  </span>
            </button>`;
      return html;
    },
    commentBoxCell: function (params) {
      let slug = "";
      let colName = params.colDef.field;
      if (params.data === undefined) {
        return params.value;
      }
      if(params.data && params.data[`${colName}.slug`]){
        slug = params.data[`${colName}.slug`]
      }
      const commentButtonTemplate = document.querySelector('[data-comment-button-template]')
      const commentButton = commentButtonTemplate.cloneNode(true)
      commentButton.setAttribute("hx-vals", `{"key": "${params.value}", "formname": "${slug}" }`);
      return commentButton;
    },
    badgeCell: function (params) {
      let bgBadgeColor = "bg-gray-100";
      let textBadgeColor = "text-gray-700";
      let colBadge = params.colDef.field;
      let html = '';
      let iconHtml = '';
      const commonStatusBadge = ag.resolveCommonStatusBadge(params.colDef, params.value);
    
      if (commonStatusBadge) {
        bgBadgeColor = commonStatusBadge.bgBadgeColor;
        textBadgeColor = commonStatusBadge.textBadgeColor;
        iconHtml = `<span class="mr-1 flex items-center">${commonStatusBadge.icon}</span>`;
      } else if (params.data) {
        if (params.data[`${colBadge}.bgBadgeColor`]) {
          bgBadgeColor = params.data[`${colBadge}.bgBadgeColor`];
        }
        if (params.data[`${colBadge}.textBadgeColor`]) {
          textBadgeColor = params.data[`${colBadge}.textBadgeColor`];
        }
        if (params.data[`${colBadge}.icon`]) {
          iconHtml = `<span class="mr-1 flex items-center">${params.data[`${colBadge}.icon`]}</span>`;
        }
      }
      if (params.value === undefined || params.value == null) {
        html = '';
      } else if (params.colDef.tv) {
        // TV mode - larger badge with more padding
        html = `
          <span class="inline-flex items-center justify-center rounded-full ${bgBadgeColor} px-1 min-w-[4.5rem] text-center text-xs font-normal ${textBadgeColor}" style="font-size: 1rem !important;">
            ${iconHtml}${params.value}
          </span>
        `;
      } else {
        html = `
          <span class="inline-flex items-center rounded-full ${bgBadgeColor} px-1 text-xs font-normal ${textBadgeColor}">
            ${iconHtml}${params.value}
          </span>
        `;
      }

      return html;
    },
    // Slim progress meter for percentage columns (0-100). Optional per-row
    // sibling "<field>.tone" carries a CSS colour for the fill; defaults to
    // the standard blue.
    progressCell: function (params) {
      if (params.value === undefined || params.value === null || params.value === '') return '';
      var raw = String(params.value).replace(/,/g, '').trim();
      var pct = Number(raw.endsWith('%') ? raw.slice(0, -1) : raw);
      if (!isFinite(pct)) return '';
      pct = Math.max(0, Math.min(100, pct));
      var fillPct = params.reverse ? 100 - pct : pct;
      var label = raw.endsWith('%') ? raw : Math.round(pct) + '%';
      var tone = (params.data && params.data[`${params.colDef.field}.tone`]) || '#3b82f6';
      return '<div class="flex h-full items-center gap-2 pl-1">' +
        '<div class="h-1.5 w-16 min-w-10 overflow-hidden rounded-full bg-slate-200">' +
        '<div class="h-full rounded-full" style="width:' + fillPct + '%;background-color:' + tone + '"></div>' +
        '</div>' +
        '<span class="text-xs text-slate-500">' + label + '</span>' +
        '</div>';
    },
    sectionsBadgeCell: function (params) {
      if (params.value === undefined || params.value == null || params.value === '') {
        return '';
      }
      
      // Split the comma-separated sections
      const sections = params.value.split(',').map(s => s.trim()).filter(s => s);
      
      if (sections.length === 0) {
        return '';
      }
      
      // Define badge colors for different sections
      const badgeColors = [
        'bg-blue-500 text-white',
        'bg-green-500 text-white', 
        'bg-purple-500 text-white',
        'bg-yellow-400 text-gray-800',
        'bg-red-500 text-white',
        'bg-indigo-500 text-white',
        'bg-pink-500 text-white',
        'bg-orange-500 text-white',
        'bg-teal-500 text-white',
        'bg-cyan-500 text-white'
      ];
      
      // Create badges for each section
      const badges = sections.map((section, index) => {
        const colorIndex = index % badgeColors.length;
        const badgeClass = badgeColors[colorIndex];
        return `<span class="inline-flex items-center rounded-full ${badgeClass} px-1 py-0 text-xs font-medium mr-0.5 mb-0.5">${section}</span>`;
      });
      
      return `<div class="flex flex-wrap gap-1">${badges.join('')}</div>`;
    },
    nameWithDefaultBadge: function (params) {
      const name = params.value == null ? '' : String(params.value);
      let show = false;
      try {
        const v = params?.data?.isdefault;
        show = v === 1 || v === true || v === '1' || String(v).toLowerCase() === 'true';
      } catch (e) { show = false }
      if (!show) {
        return name;
      }
      const badgeHtml = `<span class="ml-2 inline-flex items-center rounded-md bg-green-600 text-white ring-1 ring-green-700 px-1 text-[10px] font-normal">Default</span>`;
      return `<span>${name}</span>${badgeHtml}`;
    },
    manualAlertBadgeCell: function (params) {

      const label = String(params.value ?? '').replace(/[&<>"']/g, (ch) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
      ));
      const flag = params?.data?.[`${params.colDef.field}.manualAlert`];
      const isManual = flag === 1 || flag === true || flag === '1' || String(flag).toLowerCase() === 'true';
      if (!isManual) {
        return label;
      }
      const badgeHtml = `<span class="ml-2 inline-flex items-center rounded-full bg-orange-100 text-orange-700 px-1 text-[10px] font-normal">Manual alert</span>`;
      return `<span>${label}</span>${badgeHtml}`;
    },
    djangoFormCell: function (params) {
      const value = (params.valueFormatted != null) ? String(params.valueFormatted)
        : (params.value !== null && params.value !== undefined) ? String(params.value) : '';
      if (params.data === undefined) {
        return value;
      } 
      const errKey = `${params.colDef.field}.error`;
      if (params.data[errKey] === undefined) {
        return value
      }
      let msg = params.data[errKey].reduce((acc, err) => {
        return acc + `\n${err.message}`
      }, '');
      return `
      <span style="background-color: #eea7ab;" class="w-full p-2 rounded-md">
          ${msg}
      </span>`;
    },
    statusCellRenderer: function (params){
      const status = params.value;
      let tailwindClass = '';
      let statusText = '';

      if (status === "succeed") {
        tailwindClass = 'bg-green-500';
        statusText = 'Succeed';
      } else if (status === "failed") {
        tailwindClass = 'bg-red-500';
        statusText = 'Failed';
      } else {
        tailwindClass = 'bg-yellow-500';
        statusText = 'Pending';
      }

      if (!status) return '';

      return `
        <div class="flex items-center space-x-2">
          <div class="${tailwindClass} rounded-full w-4 h-4"></div>
          <span class="font-semibold">${statusText}</span>
        </div>
      `;
    },
    coloredCell: function (params) {
      let textColor = ""
      let textIcon = ""
      let col = params.colDef.field;
      let html = '';

      if (params.data) {
        if (params.data[`${col}.textColor`]) {
          textColor = params.data[`${col}.textColor`];
        }
        if (params.data[`${col}.textIcon`]) {
          textIcon = params.data[`${col}.textIcon`];
        }
      }

      if (params.value !== undefined && params.value !== null) {
        html = `<span class="inline-flex items-center ${textColor} px-1 text-xs font-medium">${textIcon} ${params.value}</span>`;
      }

      return html;
    },
    buttonCell: function (params) {
      const onclickAtt = params.colDef.attributes.onclick;
      let click = null;
      if(!params.data) return '';
      if (
        typeof onclickAtt === 'string' &&
        onclickAtt.startsWith('window.') &&
        window[onclickAtt.slice('window.'.length)]
      ) {
        click = onclickAtt.slice('window.'.length)
      }
      const row_idx = params.data?.__index__
      return `<button onclick="${click ? `(${click.toString()})(${row_idx})` : ''}" class='text-red-700 rounded'>
        ✖ </button>`
    }
  },
  attachCellRenderFunctions: function (columnsDefs) {
    // List of built-in AG Grid cell renderers that don't need custom registration
    const builtInRenderers = [
      'agCheckboxCellRenderer',
    ];

    for (var columnsDefsIndex = 0; columnsDefsIndex < columnsDefs.length; ++columnsDefsIndex) {
      var column = columnsDefs[columnsDefsIndex];
      if (column.cellRenderer === undefined && this.isStatusBadgeColumn(column)) {
        column.cellRenderer = 'badgeCell';
      }
      if (column.cellRenderer === undefined) {
        continue
      }
      
      // Skip built-in AG Grid renderers - they're handled automatically
      if (builtInRenderers.includes(column.cellRenderer)) {
        continue;
      }
      
      if (column.cellRenderer && window[column.cellRenderer]) {
        column.cellRenderer = window[column.cellRenderer];
      } else if (column.cellRenderer && window.ag.cellRenderer[column.cellRenderer]) {
        column.cellRenderer = window.ag.cellRenderer[column.cellRenderer];
      } else {
        console.error(`Missing: ${column.cellRenderer}`)
        delete column.cellRenderer
      }
    }
    return columnsDefs;
  },

  // Lightweight UI helpers to toggle print layout from within the grid
  _ensurePrintButtonStyles: function () { return gridPrint._ensurePrintButtonStyles(); },
  _injectPrintButtons: function (el) { return gridPrint._injectPrintButtons(el); },

  // ----- Print helpers (AG Grid print layout) ---------------------------------
  _resolveGridElement: function (elOrId) { return gridPrint._resolveGridElement(elOrId); },

  _relaxAncestorsForPrint: function (el) { return gridPrint._relaxAncestorsForPrint(el); },

  _restoreAncestorsAfterPrint: function (el) { return gridPrint._restoreAncestorsAfterPrint(el); },

  // Hide all non-grid siblings up the DOM chain to remove layout gaps when printing
  _hideNonGridSiblings: function (el) { return gridPrint._hideNonGridSiblings(el); },

  // Restore hidden siblings after printing
  _restoreHiddenSiblings: function (el) { return gridPrint._restoreHiddenSiblings(el); },

  // Spinner overlay helpers
  _showPrintSpinner: function (el) { return gridPrint._showPrintSpinner(el); },
  _hidePrintSpinner: function (el) { return gridPrint._hidePrintSpinner(el); },

  setPrintLayout: function (elOrId) { return gridPrint.setPrintLayout(elOrId); },

  resetPrintLayout: function (elOrId) { return gridPrint.resetPrintLayout(elOrId); },

  printGrid: function (elOrId, waitMs = 300) { return gridPrint.printGrid(elOrId, waitMs); }
}

// Global function for handling selection changes with hx-vals integration
window.setHxValsOnSelectionChange = function(event) {
  try {
    const selectedRows = event.api.getSelectedRows();
    const selectedRowsJson = JSON.stringify(selectedRows);
    
    // Find the grid element using the grid container
    const gridContainer =
      (typeof event.api.getGridElement === 'function' ? event.api.getGridElement() : null) ||
      event.api.getGridOption('el') ||
      document.querySelector('.ag-root-wrapper')?.closest('[data-ag-id]') ||
      document.querySelector('[data-ag-id]');
    
    if (gridContainer) {
      // Update the grid element's dataset
      gridContainer.dataset.selectedRows = selectedRowsJson;
      
      // Update target element if specified
      const targetSelector =
        event.api.getGridOption('agOnRowSelectedTarget') ||
        gridContainer.dataset.agOnRowSelectedTarget;
      if (targetSelector) {
        const target = document.querySelector(targetSelector);
        if (target) {
          target.value = selectedRowsJson;
          target.setAttribute('data-selected-rows', selectedRowsJson);
          
          // Trigger change event for any listeners
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    
    // Always try to find and update idsSelected elements
    const idsSelectedElements = document.querySelectorAll('input[name="idsSelected"], input[data-formname][name="idsSelected"]');
    idsSelectedElements.forEach(element => {
      element.value = selectedRowsJson;
      element.setAttribute('data-selected-rows', selectedRowsJson);
    });

    // Also update listedDeleted inputs (used by some SSR dataforms)
    const listedDeletedElements = document.querySelectorAll('input[name="listedDeleted"], input[data-formname][name="listedDeleted"]');
    listedDeletedElements.forEach(element => {
      element.value = selectedRowsJson;
      element.setAttribute('data-selected-rows', selectedRowsJson);
    });
    
    console.log('Selection updated:', selectedRows.length, 'rows selected');
  } catch (error) {
    console.error('Error in setHxValsOnSelectionChange:', error);
  }
};

// AG Grid v34 set-filter regression: clicks on the checkbox area don't trigger
// toggle, but clicks on the row label do. Forward checkbox-area clicks to the label.
document.addEventListener('click', function (event) {
  if (event._agFilterRouted) return;
  const wrapper = event.target?.closest?.('.ag-checkbox-input-wrapper');
  if (!wrapper || !wrapper.closest('.ag-set-filter, .ag-filter')) return;
  const row = wrapper.closest('.ag-set-filter-item, .ag-virtual-list-item');
  const label = row && row.querySelector('.ag-input-field-label');
  if (!label) return;
  event.preventDefault();
  event.stopPropagation();
  const forwarded = new MouseEvent('click', { bubbles: true });
  forwarded._agFilterRouted = true;
  label.dispatchEvent(forwarded);
}, true);

window.ag = ag;
